import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../features/auth/authSlice';
import { logoutUser } from '../services/authService';
import {
  getUsers, createUser, deactivateUser,
  getAdminReportGroups, createReportGroup,
  getAdminReports, createReport,
  addReportToGroup, removeReportFromGroup, reorderGroupReports,
  addUserToGroup, removeUserFromGroup,
  getPowerBiReports, toggleCanAccessTest, assignTempPassword,
  getReportContexts, createReportContext, updateReportContext, deleteReportContext, assignContextToReport,
  getTenants, createTenant, updateTenant, toggleTenant, deleteTenant,
} from '../services/adminService';
import {
  getTeams, createTeam, updateTeam,
  getRegions, createRegion,
  getReps, createRep, updateRep,
  getRepBricks, createRepBrick, closeRepBrick, deleteRepBrick,
  getMarkets, createMarket, updateMarket, deleteMarket,
  getProducts, createProduct, updateProduct, deleteProduct, bulkUpdateProducts,
  assignProductToMarket, removeProductFromMarket,
  getGeography, bulkImportSalesForce,
  bulkImportProductsMarkets,getTenantLabels,toggleAiChatForGroup,
  refreshReportDataset, getRefreshStatus,
  getEnabledApplications, getTenantApplications, setTenantApplication,
} from '../services/adminService';
import apiClient from '../services/apiClient';
import { apiErrorMessage } from '../utils/apiError';
import TempPasswordModal from '../components/TempPasswordModal';
import RepRetireModal from '../components/RepRetireModal';
import RepHandoverModal from '../components/RepHandoverModal';
import BrickAssignmentPanel from './BrickAssignmentPanel';
import ProductTargetPanel from './ProductTargetPanel';
import TargetDistributionPanel from './TargetDistributionPanel';
import EtlPage from './EtlPage';
const ITEMS_PER_PAGE = 10;

// Ekip/bölge silme, alt kayıtların (bölge, temsilci, brick ataması) dönem kapatma
// kuralları tasarlanana kadar devre dışı. Temsilci silme RepRetireModal üzerinden yapılır.
const DELETE_DISABLED_HINT = 'Ekip/bölge silme, devir kuralları tasarlanana kadar devre dışı.';

// Aranabilir pazar seçici — ürünü pazara dahil ederken çok sayıda pazar
// arasından hızlı seçim. options alfabetik sıralı gelmeli; onPick(marketId).
const MarketPicker = ({ options, onPick }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? options.filter(o => `${o.marketName} ${o.teamName}`.toLowerCase().includes(needle))
    : options;

  if (!open) {
    return (
      <button
        style={{ ...styles.selectSmall, fontSize: '11px', width: '100%', textAlign: 'left', cursor: 'pointer', background: '#fff' }}
        onClick={() => setOpen(true)}>
        + Pazar Ekle
      </button>
    );
  }

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <input
        autoFocus
        placeholder="🔍 Pazar ara..."
        value={q}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); setQ(''); } }}
        style={{ ...styles.selectSmall, fontSize: '11px', width: '100%', boxSizing: 'border-box' }}
      />
      <div style={{ marginTop: '2px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '6px 8px', fontSize: '11px', color: '#94a3b8' }}>Sonuç yok</div>
        ) : filtered.map(o => (
          <div
            key={o.id}
            onClick={() => { onPick(o.id); setOpen(false); setQ(''); }}
            style={{ padding: '5px 8px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
            {o.marketName} <span style={{ color: '#94a3b8' }}>({o.teamName})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('users');
  const [enabledApps, setEnabledApps] = useState([]);
  const [editingTenantApps, setEditingTenantApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportGroups, setReportGroups] = useState([]);
  const [dragReport, setDragReport] = useState(null); // { groupId, reportId } — sürüklenen rapor
  const [reports, setReports] = useState([]);
  const [reportContexts, setReportContexts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [powerBiReports, setPowerBiReports] = useState([]);
  const [powerBiLoading, setPowerBiLoading] = useState(false);

  // Arama
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [groupEnvFilter, setGroupEnvFilter] = useState('');
  const [reportSearch, setReportSearch] = useState('');
  const [reportEnvFilter, setReportEnvFilter] = useState('');
  const [contextSearch, setContextSearch] = useState('');
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');

  // Sayfalama
  const [userPage, setUserPage] = useState(1);
  // Toplu geçici şifre atama (yalnızca SuperAdmin)
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const [assigningPassword, setAssigningPassword] = useState(false);
  const [groupPage, setGroupPage] = useState(1);
  const [reportPage, setReportPage] = useState(1);
  const [contextPage, setContextPage] = useState(1);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [tenantPage, setTenantPage] = useState(1);

  // Kullanıcı formu
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFullName, setNewUserFullName] = useState('');
  const [newUserTenantId, setNewUserTenantId] = useState('');

  // Rapor grubu formu
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [newGroupEnv, setNewGroupEnv] = useState('prod');
  const [newGroupTenantId, setNewGroupTenantId] = useState('');
  const [newGroupAiChat, setNewGroupAiChat] = useState(true);
  const [groupTenantFilter, setGroupTenantFilter] = useState('');
  
  // Rapor formu
  const [newReportName, setNewReportName] = useState('');
  const [newReportId, setNewReportId] = useState('');
  const [newReportDesc, setNewReportDesc] = useState('');
  const [newReportEnv, setNewReportEnv] = useState('prod');
  const [newReportTenantId, setNewReportTenantId] = useState('');
  const [reportTenantFilter, setReportTenantFilter] = useState('');
  const [refreshStatuses, setRefreshStatuses] = useState({});  // { reportId: 'Unknown' | 'Completed' | 'Failed' }
  const [refreshingIds, setRefreshingIds] = useState({});       // { reportId: true } — buton loading

  // Context formu
  const [newContextName, setNewContextName] = useState('');
  const [newContextDimensions, setNewContextDimensions] = useState('');
  const [newContextMeasures, setNewContextMeasures] = useState('');
  const [newContextRules, setNewContextRules] = useState('');
  const [editingContext, setEditingContext] = useState(null);

  // Tenant formu
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantDesc, setNewTenantDesc] = useState('');
  const [newTenantWorkspaceId, setNewTenantWorkspaceId] = useState('');
  const [newTenantTestWorkspaceId, setNewTenantTestWorkspaceId] = useState('');
  const [editingTenant, setEditingTenant] = useState(null);
  const [newTenantPrimaryColor, setNewTenantPrimaryColor] = useState('#1a1a2e');
  const [newTenantSecondaryColor, setNewTenantSecondaryColor] = useState('#2e75b6');
  const [newTenantLogoUrl, setNewTenantLogoUrl] = useState('');
  const [newTenantFaviconUrl, setNewTenantFaviconUrl] = useState('');
  const [newTenantPortalTitle, setNewTenantPortalTitle] = useState('');
  const [newTenantTextColor, setNewTenantTextColor] = useState('#ffffff');
  const [newTenantFontBold, setNewTenantFontBold] = useState(false);
  const [newTenantFontItalic, setNewTenantFontItalic] = useState(false);
  const [newTenantFontSize, setNewTenantFontSize] = useState(16);

  // Master Data
  const [mdSubTab, setMdSubTab] = useState('salesforce');
  const [mdTenantId, setMdTenantId] = useState('');
  const [teams, setTeams] = useState([]);
  const [regions, setRegions] = useState([]);
  const [reps, setReps] = useState([]);
  const [repBricks, setRepBricks] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [geography, setGeography] = useState([]);
  const [mdLoading, setMdLoading] = useState(false);
  const [sfLabels, setSfLabels] = useState({});
  const [pmLabels, setPmLabels] = useState({});
  
  // Saha ekibi bulk import
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);

  // Ürün & Pazar bulk import
  const [pmFile, setPmFile] = useState(null);
  const [pmLoading, setPmLoading] = useState(false);
  const [pmResult, setPmResult] = useState(null);
  const [pmErrors, setPmErrors] = useState([]);

  // Tekil formlar
  const [newTeamName, setNewTeamName] = useState('');
  const [newRegionName, setNewRegionName] = useState('');
  const [newRegionTeamId, setNewRegionTeamId] = useState('');
  const [newRepName, setNewRepName] = useState('');
  const [newRepCode, setNewRepCode] = useState('');
  const [newRepRegionId, setNewRepRegionId] = useState('');
  const [editingRep, setEditingRep] = useState(null);
  // Silinmek üzere seçilen temsilci — brick devir modalını açar.
  const [retiringRep, setRetiringRep] = useState(null);
  // Brick'leri başka temsilcilere devredilecek temsilci — devir modalını açar.
  const [handoverRep, setHandoverRep] = useState(null);
  const [newMarketName, setNewMarketName] = useState('');
  const [newMarketTeamId, setNewMarketTeamId] = useState('');
  const [newMarketWeekly, setNewMarketWeekly] = useState(true);
  const [newMarketMonthly, setNewMarketMonthly] = useState(true);
  const [regionTeamFilter, setRegionTeamFilter] = useState('');
  const [regionSort, setRegionSort] = useState({ col: 'teamName', dir: 'asc' });
  const [repTeamFilter, setRepTeamFilter] = useState('');
  const [repRegionFilter, setRepRegionFilter] = useState('');
  const [repSort, setRepSort] = useState({ col: 'teamName', dir: 'asc' });

  // Pazar filtre & sıralama & düzenleme
  const [marketTeamFilter, setMarketTeamFilter] = useState('');
  const [marketSort, setMarketSort] = useState({ col: 'marketName', dir: 'asc' });
  const [editingMarket, setEditingMarket] = useState(null);

  //Tenant bazlı master data labels
  const SF_LABEL_KEYS = ['team','region','salesRepUniqueId','salesRep','brick','salesPercent','startDate','endDate','whiteFlag'];
  const PM_LABEL_KEYS = ['team','market','imsProduct','productShort','productGroupName','avixaProduct','suMultiplier','group','promoNonPromo'];

  const SF_DEFAULTS = { team:'Team', region:'Region', salesRepUniqueId:'SalesRepUniqueId', salesRep:'SalesRep', brick:'Brick', salesPercent:'Salespercent', startDate:'StartDate', endDate:'EndDate', whiteFlag:'WhiteFlag' };
  const PM_DEFAULTS = { team:'TEAM', market:'MARKET', imsProduct:'IMSPRODUCT', productShort:'PRODUCTSHORT', 
                        productGroupName:'PRODUCTGROUPNAME', avixaProduct:'AVIXAPRODUCT',
                        suMultiplier:'SU MULTIPLIER', group:'Group', promoNonPromo:'Promo/Non Promo',
                        isAvixaProduct:'Avixa Ürünü',avixaProductName:'Avixa Ürün Adı'};

  const pm = (key) => pmLabels[key] ?? pmLabels[key.toLowerCase()] ?? PM_DEFAULTS[key];
  const sf = (key) => sfLabels[key] ?? sfLabels[key.toLowerCase()] ?? SF_DEFAULTS[key];

  // NOT: Giriş tipi burada etiket override'ından çıkarılıyor — etiket varsayılandan
  // farklıysa kolon farklı render edilir. Etiket ve giriş-tipi aslında bağımsız
  // kavramlar; farklı davranış/seçenek isteyen 3. bir tenant gelirse bu mantığı
  // açık per-kolon config'e (type + options) genelleştir.
  const groupTypeIsText   = pm('group') !== PM_DEFAULTS.group;
  const promoIsFocusBrand = pm('promoNonPromo') !== PM_DEFAULTS.promoNonPromo;

  // Ürün filtre & yeni satır
  const [productTeamFilter, setProductTeamFilter] = useState('');
  const [productMarketFilter, setProductMarketFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showNewProductRow, setShowNewProductRow] = useState(false);
  const [newProductRow, setNewProductRow] = useState({
    imsProductName: '', avixaProductName: '', productGroupName: '',isVisible: true, isAvixaProduct: false, suMultiplier: 1, groupType: '', promoFlag: '', showInWeeklyDashboard: true, showInMonthlyDashboard: true
  });

  const loadMasterData = async (tid) => {
    if (isSuperAdmin && !tid) return;
    setMdLoading(true);
    try {
      const [t, r, rp, rb, m, p, g] = await Promise.all([
        getTeams(tid), getRegions(tid), getReps(tid), getRepBricks(tid),
        getMarkets(tid), getProducts(tid), getGeography(tid)
      ]);
      setTeams(t); setRegions(r); setReps(rp); setRepBricks(rb);
      setMarkets(m); setProducts(p); setGeography(g);
    } catch {
      setMessage('Master data yüklenirken hata oluştu.');
    } finally {
      setMdLoading(false);
    }
  };

  const loadPowerBiReports = useCallback(async (environment, tenantId) => {
    if (isSuperAdmin && !tenantId) { setPowerBiReports([]); return; }
    setPowerBiLoading(true);
    try {
      const result = await getPowerBiReports(environment, tenantId || null);
      setPowerBiReports(result);
    } catch { setPowerBiReports([]); }
    finally { setPowerBiLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => { loadAll(); }, [isSuperAdmin]); // eslint-disable-line
  useEffect(() => {
    if (activeTab === 'masterdata') {
      const tid = isSuperAdmin ? (mdTenantId || null) : user?.tenantId;
      loadMasterData(tid);
    }
  }, [activeTab, mdTenantId]); // eslint-disable-line
  useEffect(() => {
    if (activeTab === 'feedbacks' && feedbacks.length === 0) loadFeedbacks();
    if (activeTab === 'tenants' && tenants.length === 0) loadTenants();
    if (activeTab === 'target-distribution' && isSuperAdmin && tenants.length === 0) loadTenants();
  }, [activeTab]); // eslint-disable-line
  // Tenant→Uygulama görünürlüğü (sekme gating). Normal admin: token'dan; SuperAdmin: seçili tenant.
  useEffect(() => {
    const appTid = isSuperAdmin ? (mdTenantId || null) : null;
    if (isSuperAdmin && !appTid) { setEnabledApps([]); return; }
    getEnabledApplications(appTid).then(setEnabledApps).catch(() => setEnabledApps([]));
  }, [mdTenantId, isSuperAdmin]);
  // Tenant düzenlenirken o tenant'ın uygulama aç/kapa durumunu yükle.
  useEffect(() => {
    if (editingTenant?.id) {
      getTenantApplications(editingTenant.id).then(setEditingTenantApps).catch(() => setEditingTenantApps([]));
    } else {
      setEditingTenantApps([]);
    }
  }, [editingTenant]);
  useEffect(() => {
    if (activeTab === 'reports') {
      const tenantId = isSuperAdmin ? (newReportTenantId || null) : null;
      loadPowerBiReports(newReportEnv, tenantId);
      setNewReportId(''); setNewReportName('');
    }
  }, [newReportEnv, newReportTenantId, activeTab]); // eslint-disable-line
 
  useEffect(() => {
    const tid = isSuperAdmin ? mdTenantId : user?.tenantId;
    if (!tid) { setSfLabels({}); setPmLabels({}); return; }

    const toLowerKeys = (obj) => Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v])
    );

    getTenantLabels(tid, 'salesforce_bulk_upload')
      .then(d => setSfLabels(toLowerKeys(d))).catch(() => setSfLabels({}));
    getTenantLabels(tid, 'product_bulk_upload')
      .then(d => setPmLabels(toLowerKeys(d))).catch(() => setPmLabels({}));
  }, [mdTenantId, isSuperAdmin, user?.tenantId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const promises = [getUsers(), getAdminReportGroups(), getAdminReports(), getReportContexts()];
      if (isSuperAdmin) promises.push(getTenants());
      const results = await Promise.all(promises);
      setUsers(results[0]); setReportGroups(results[1]);
      setReports(results[2]); setReportContexts(results[3]);
      if (isSuperAdmin) setTenants(results[4]);
    } catch { setMessage('Veriler yüklenirken hata oluştu.'); }
    finally { setLoading(false); }
  };

  const loadFeedbacks = async () => {
    try { const r = await apiClient.get('/feedback'); setFeedbacks(r.data); }
    catch { setMessage('Feedback verileri yüklenemedi.'); }
  };

  const loadTenants = async () => {
    try { setTenants(await getTenants()); }
    catch { setMessage('Tenant verileri yüklenemedi.'); }
  };

  const filteredUsers = users.filter(u =>
    (u.fullName?.toLowerCase().includes(userSearch.toLowerCase()) ||
     u.email?.toLowerCase().includes(userSearch.toLowerCase())) &&
    (!showPendingOnly || !u.lastLoginAt)
  );
  const filteredGroups = reportGroups.filter(g => {
    if (isSuperAdmin && groupTenantFilter && String(g.tenantId) !== groupTenantFilter) return false;
    if (groupEnvFilter && g.environment !== groupEnvFilter) return false;
    if (!isSuperAdmin && g.tenantId !== user?.tenantId) return false; // normal admin güvencesi
    return g.name?.toLowerCase().includes(groupSearch.toLowerCase());
  });
  const filteredReports = reports.filter(r => {
    if (isSuperAdmin && reportTenantFilter && String(r.tenantId) !== reportTenantFilter) return false;
    if (!isSuperAdmin && r.tenantId !== user?.tenantId) return false;
    if (reportEnvFilter && r.environment !== reportEnvFilter) return false;
    return r.name?.toLowerCase().includes(reportSearch.toLowerCase());
  });
  const filteredContexts = reportContexts.filter(c => c.name?.toLowerCase().includes(contextSearch.toLowerCase()));
  const filteredFeedbacks = feedbacks.filter(f =>
    f.userMessage?.toLowerCase().includes(feedbackSearch.toLowerCase()) ||
    f.userEmail?.toLowerCase().includes(feedbackSearch.toLowerCase()) ||
    f.userComment?.toLowerCase().includes(feedbackSearch.toLowerCase())
  );
  const filteredTenants = tenants.filter(t => t.name?.toLowerCase().includes(tenantSearch.toLowerCase()));

  const paginate = (items, page) => items.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = (items) => Math.ceil(items.length / ITEMS_PER_PAGE);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(newUserEmail, newUserFullName, [], isSuperAdmin ? parseInt(newUserTenantId) || null : user?.tenantId);
      setMessage('Kullanıcı oluşturuldu. Aktivasyon maili gönderildi.');
      setNewUserEmail(''); setNewUserFullName(''); setNewUserTenantId('');
      loadAll();
    } catch (err) { setMessage(err.response?.data?.message || err.message || 'Kullanıcı oluşturulamadı.'); }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleAssignTempPassword = async () => {
    if (selectedUserIds.length === 0) return;
    setAssigningPassword(true);
    try {
      const result = await assignTempPassword(selectedUserIds);
      setTempPasswordResult(result);
      setSelectedUserIds([]);
      setMessage(`${result.affectedCount} kullanıcıya geçici şifre atandı.`);
      loadAll();
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Şifre atanamadı.');
    } finally {
      setAssigningPassword(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Kullanıcıyı deaktif etmek istiyor musunuz?')) return;
    try { await deactivateUser(userId); setMessage('Kullanıcı deaktif edildi.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };

  const handleToggleCanAccessTest = async (userId) => {
    try { await toggleCanAccessTest(userId); setMessage('Test erişimi güncellendi.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await createReportGroup(newGroupName, newGroupDesc, isSuperAdmin ? parseInt(newGroupTenantId) || null : user?.tenantId, newGroupEnv);
      setMessage(`Rapor grubu oluşturuldu.`);
      setNewGroupName(''); setNewGroupDesc(''); setNewGroupTenantId(''); setNewGroupEnv('prod');
      loadAll();
    } catch { setMessage('Rapor grubu oluşturulamadı.'); }
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    try {
      const selectedPbReport = powerBiReports.find(r => r.id === newReportId);
      await createReport(newReportName, newReportId, newReportDesc, newReportEnv,
        selectedPbReport?.workspaceId || '', isSuperAdmin ? parseInt(newReportTenantId) || null : user?.tenantId);
      setMessage(`Rapor oluşturuldu.`);
      setNewReportName(''); setNewReportId(''); setNewReportDesc('');
      loadAll();
    } catch { setMessage('Rapor oluşturulamadı.'); }
  };

  const handleAddReportToGroup = async (groupId, reportId) => {
    try { await addReportToGroup(groupId, reportId); setMessage('Rapor gruba eklendi.'); loadAll(); }
    catch (err) { setMessage(err.response?.data?.message || 'İşlem başarısız.'); }
  };
  const handleRemoveReportFromGroup = async (groupId, reportId) => {
    try { await removeReportFromGroup(groupId, reportId); setMessage('Rapor gruptan çıkarıldı.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };

  // Grup içi rapor sıralaması (sürükle-bırak).
  const handleReorderReports = async (groupId, orderedIds) => {
    // Optimistik: yerel state'i hemen güncelle.
    setReportGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      const byId = new Map((g.reports || []).map(r => [r.id, r]));
      return { ...g, reports: orderedIds.map(id => byId.get(id)).filter(Boolean) };
    }));
    try { await reorderGroupReports(groupId, orderedIds); setMessage('Sıralama güncellendi.'); }
    catch { setMessage('Sıralama güncellenemedi.'); loadAll(); }
  };

  const handleReportDrop = (groupId, targetReportId) => {
    if (!dragReport || dragReport.groupId !== groupId || dragReport.reportId === targetReportId) {
      setDragReport(null); return;
    }
    const group = reportGroups.find(g => g.id === groupId);
    if (!group) { setDragReport(null); return; }
    const ids = (group.reports || []).map(r => r.id);
    const from = ids.indexOf(dragReport.reportId);
    const to = ids.indexOf(targetReportId);
    setDragReport(null);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    handleReorderReports(groupId, ids);
  };
  const handleAddUserToGroup = async (userId, groupId) => {
    try { await addUserToGroup(userId, groupId); setMessage('Kullanıcı gruba eklendi.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };
  const handleRemoveUserFromGroup = async (userId, groupId) => {
    try { await removeUserFromGroup(userId, groupId); setMessage('Kullanıcı gruptan çıkarıldı.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };

  const handleCreateContext = async (e) => {
    e.preventDefault();
    try {
      if (editingContext) {
        await updateReportContext(editingContext.id, newContextName, newContextDimensions, newContextMeasures, newContextRules);
        setMessage('Context güncellendi.'); setEditingContext(null);
      } else {
        await createReportContext(newContextName, newContextDimensions, newContextMeasures, newContextRules);
        setMessage('Context oluşturuldu.');
      }
      setNewContextName(''); setNewContextDimensions(''); setNewContextMeasures(''); setNewContextRules('');
      loadAll();
    } catch { setMessage('İşlem başarısız.'); }
  };
  const handleEditContext = (context) => {
    setEditingContext(context);
    setNewContextName(context.name); setNewContextDimensions(context.dimensionDefinitions);
    setNewContextMeasures(context.measureDefinitions); setNewContextRules(context.systemPromptRules || '');
    window.scrollTo(0, 0);
  };
  const handleDeleteContext = async (id) => {
    if (!window.confirm('Context silinsin mi?')) return;
    try { await deleteReportContext(id); setMessage('Context silindi.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };
  const handleAssignContext = async (reportId, contextId) => {
    try { await assignContextToReport(reportId, parseInt(contextId) || 0); setMessage('Context rapora atandı.'); loadAll(); }
    catch { setMessage('İşlem başarısız.'); }
  };

  const resetTenantForm = () => {
    setNewTenantName(''); setNewTenantDesc('');
    setNewTenantWorkspaceId(''); setNewTenantTestWorkspaceId('');
    setNewTenantPrimaryColor('#1a1a2e'); setNewTenantSecondaryColor('#2e75b6');
    setNewTenantLogoUrl(''); setNewTenantFaviconUrl(''); setNewTenantPortalTitle('');
    setNewTenantTextColor('#ffffff'); setNewTenantFontBold(false);
    setNewTenantFontItalic(false); setNewTenantFontSize(16);
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await updateTenant(
          editingTenant.id, newTenantName, newTenantDesc,
          newTenantWorkspaceId, newTenantTestWorkspaceId || null,
          newTenantPrimaryColor, newTenantSecondaryColor,
          newTenantLogoUrl || null, newTenantFaviconUrl || null,
          newTenantPortalTitle || null,
          newTenantTextColor || null, newTenantFontBold, newTenantFontItalic, newTenantFontSize || null
        );
        setMessage('Tenant güncellendi.'); setEditingTenant(null);
      } else {
        await createTenant(
          newTenantName, newTenantDesc,
          newTenantWorkspaceId, newTenantTestWorkspaceId || null,
          newTenantPrimaryColor, newTenantSecondaryColor,
          newTenantLogoUrl || null, newTenantFaviconUrl || null,
          newTenantPortalTitle || null,
          newTenantTextColor || null, newTenantFontBold, newTenantFontItalic, newTenantFontSize || null
        );
        setMessage('Tenant oluşturuldu.');
      }
      resetTenantForm();
      loadTenants();
    } catch { setMessage('İşlem başarısız.'); }
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setNewTenantName(tenant.name); setNewTenantDesc(tenant.description || '');
    setNewTenantWorkspaceId(tenant.powerBiWorkspaceId);
    setNewTenantTestWorkspaceId(tenant.powerBiTestWorkspaceId || '');
    setNewTenantPrimaryColor(tenant.primaryColor || '#1a1a2e');
    setNewTenantSecondaryColor(tenant.secondaryColor || '#2e75b6');
    setNewTenantLogoUrl(tenant.logoUrl || ''); setNewTenantFaviconUrl(tenant.faviconUrl || '');
    setNewTenantPortalTitle(tenant.portalTitle || '');
    setNewTenantTextColor(tenant.textColor || '#ffffff');
    setNewTenantFontBold(tenant.fontBold || false);
    setNewTenantFontItalic(tenant.fontItalic || false);
    setNewTenantFontSize(tenant.fontSize || 16);
    window.scrollTo(0, 0);
  };

  const handleToggleTenant = async (id) => {
    try { await toggleTenant(id); setMessage('Tenant durumu güncellendi.'); loadTenants(); }
    catch { setMessage('İşlem başarısız.'); }
  };
  const handleDeleteTenant = async (id) => {
    if (!window.confirm('Tenant silinsin mi? Bu işlem geri alınamaz.')) return;
    try { await deleteTenant(id); setMessage('Tenant silindi.'); loadTenants(); }
    catch (err) { setMessage(err.response?.data?.message || 'İşlem başarısız.'); }
  };

  const handleBulkImportProductsMarkets = async () => {
    if (!pmFile) { setMessage('Lütfen bir dosya seçin.'); return; }
    setPmLoading(true); setPmResult(null); setPmErrors([]);
    try {
      const tid = isSuperAdmin ? mdTenantId : null;
      const res = await bulkImportProductsMarkets(pmFile, tid || null);
      setPmResult(res); setMessage('✅ İçe aktarma tamamlandı.');
      loadMasterData(isSuperAdmin ? mdTenantId : null);
    } catch (err) {
      const errors = err?.response?.data?.errors;
      if (errors?.length > 0) { setPmErrors(errors); setMessage('❌ Validasyon hataları bulundu.'); }
      else setMessage(err?.response?.data?.message || err?.message || 'Bir hata oluştu.');
    } finally { setPmLoading(false); }
  };

  const Pagination = ({ currentPage, total, onPageChange }) => {
    if (total <= 1) return null;
    return (
      <div style={styles.pagination}>
        <button style={styles.pageButton} onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
        {Array.from({ length: total }, (_, i) => i + 1).map(p => (
          <button key={p} style={{ ...styles.pageButton, ...(p === currentPage ? styles.pageButtonActive : {}) }} onClick={() => onPageChange(p)}>{p}</button>
        ))}
        <button style={styles.pageButton} onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === total}>›</button>
      </div>
    );
  };

  const EnvBadge = ({ env }) => (
    <span style={{ backgroundColor: env === 'test' ? '#fef3c7' : '#dcfce7', color: env === 'test' ? '#92400e' : '#166534', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
      {env === 'test' ? '🧪 Test' : '🚀 Prod'}
    </span>
  );

  const handleToggleTenantApp = async (appKey, isEnabled) => {
    if (!editingTenant?.id) return;
    try {
      await setTenantApplication(editingTenant.id, appKey, isEnabled);
      setEditingTenantApps(apps => apps.map(a => a.key === appKey ? { ...a, isEnabled } : a));
      setMessage('Uygulama erişimi güncellendi.');
      if (String(mdTenantId) === String(editingTenant.id)) {
        getEnabledApplications(mdTenantId || null).then(setEnabledApps).catch(() => {});
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || 'İşlem başarısız.');
    }
  };

  const tabs = [
    { key: 'users', label: `Kullanıcılar (${users.length})` },
    { key: 'groups', label: `Rapor Grupları (${reportGroups.length})` },
    { key: 'reports', label: `Raporlar (${reports.length})` },
    { key: 'contexts', label: `AI Modeller (${reportContexts.length})` },
    { key: 'feedbacks', label: `AI Feedbacks (${feedbacks.length})` },
    { key: 'masterdata', label: '📊 Master Data' },
    { key: 'etl', label: '🔄 ETL' },
    ...((isSuperAdmin || enabledApps.some(a => a.key === 'target-distribution' && a.isEnabled))
      ? [{ key: 'target-distribution', label: '🎯 Hedef Dağılımı' }] : []),
    ...(isSuperAdmin ? [{ key: 'tenants', label: `Tenants (${tenants.length})` }] : [])
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
<h2 style={styles.title}>
  {(() => {
    const t = tenants.find(t => String(t.id) === String(mdTenantId));
    return t ? `${t.name} — Admin Panel` : 'Admin Panel';
  })()}
</h2>
          {isSuperAdmin && <span style={styles.superAdminBadge}>⚡ Super Admin</span>}
        </div>
        <div style={styles.headerRight}>
          <span style={styles.userName}>{user?.fullName}</span>
          <button onClick={() => navigate('/')} style={styles.navButton}>Portal</button>
          <button onClick={async () => { try { await logoutUser(); } catch {} dispatch(logout()); navigate('/login'); }} style={styles.logoutButton}>Çıkış</button>
        </div>
      </div>

      <div style={styles.tabs}>
        {tabs.map(tab => (
          <button key={tab.key} style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {message && (
        <div style={styles.message} onClick={() => setMessage('')}>
          {message} <span style={{ cursor: 'pointer' }}>✕</span>
        </div>
      )}

      <TempPasswordModal result={tempPasswordResult} onClose={() => setTempPasswordResult(null)} />

      {retiringRep && (
        <RepRetireModal
          rep={retiringRep}
          repBricks={repBricks}
          reps={reps}
          regions={regions}
          tid={isSuperAdmin ? mdTenantId : null}
          onClose={() => setRetiringRep(null)}
          onDone={(msg) => {
            setRetiringRep(null);
            setMessage(msg);
            loadMasterData(isSuperAdmin ? mdTenantId : null);
          }}
        />
      )}

      {handoverRep && (
        <RepHandoverModal
          rep={handoverRep}
          repBricks={repBricks}
          reps={reps}
          regions={regions}
          tid={isSuperAdmin ? mdTenantId : null}
          onClose={() => setHandoverRep(null)}
          onDone={(msg) => {
            setHandoverRep(null);
            setMessage(msg);
            loadMasterData(isSuperAdmin ? mdTenantId : null);
          }}
        />
      )}

      <div style={styles.content}>
        {loading && <p>Yükleniyor...</p>}

        {/* KULLANICILAR */}
        {activeTab === 'users' && !loading && (
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Yeni Kullanıcı Ekle</h3>
              <form onSubmit={handleCreateUser} style={styles.form}>
                <input placeholder="Email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} style={styles.input} required type="email" />
                <input placeholder="Ad Soyad" value={newUserFullName} onChange={e => setNewUserFullName(e.target.value)} style={styles.input} required />
                {isSuperAdmin && (
                  <select style={styles.input} value={newUserTenantId} onChange={e => setNewUserTenantId(e.target.value)} required>
                    <option value="">Tenant Seçin...</option>
                    {tenants.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                <button type="submit" style={styles.primaryButton}>Oluştur</button>
              </form>
            </div>
            <div style={styles.card}>
              <div style={styles.tableHeader}>
                <h3 style={styles.cardTitle}>Kullanıcılar ({filteredUsers.length})</h3>
                <input placeholder="🔍 Ad veya email ara..." value={userSearch} onChange={e => { setUserSearch(e.target.value); setUserPage(1); }} style={styles.searchInput} />
              </div>
              {isSuperAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showPendingOnly} onChange={e => { setShowPendingOnly(e.target.checked); setUserPage(1); }} />
                    Sadece şifre bekleyenler (hiç giriş yapmamış)
                  </label>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: '13px', color: '#555' }}>{selectedUserIds.length} seçili</span>
                  <button
                    onClick={handleAssignTempPassword}
                    disabled={selectedUserIds.length === 0 || assigningPassword}
                    style={{ ...styles.primaryButton, fontSize: '13px', padding: '6px 14px', opacity: (selectedUserIds.length === 0 || assigningPassword) ? 0.5 : 1, cursor: (selectedUserIds.length === 0 || assigningPassword) ? 'not-allowed' : 'pointer' }}
                  >
                    {assigningPassword ? 'Atanıyor...' : 'Seçili kullanıcılara şifre ata'}
                  </button>
                </div>
              )}
              <table style={styles.table}>
                <thead>
                  <tr>
                    {isSuperAdmin && <th style={styles.th}></th>}
                    <th style={styles.th}>Ad Soyad</th><th style={styles.th}>Email</th><th style={styles.th}>Durum</th>
                    <th style={styles.th}>Admin</th>{isSuperAdmin && <th style={styles.th}>Tenant</th>}
                    <th style={styles.th}>Rapor Grupları</th><th style={styles.th}>Test Erişimi</th><th style={styles.th}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredUsers, userPage).map(u => (
                    <tr key={u.id}>
                      {isSuperAdmin && (
                        <td style={styles.td}>
                          {!u.lastLoginAt
                            ? <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleSelectUser(u.id)} title="Şifre atamak için seç" />
                            : <span title="Zaten giriş yapmış" style={{ color: '#bbb' }}>—</span>}
                        </td>
                      )}
                      <td style={styles.td}>{u.fullName}</td>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}><span style={{ color: u.isActive ? 'green' : 'red' }}>{u.isActive ? 'Aktif' : 'Pasif'}</span></td>
                      <td style={styles.td}>{u.isSuperAdmin ? '⚡' : u.isAdmin ? '✓' : '-'}</td>
                      {isSuperAdmin && <td style={styles.td}><span style={styles.tenantBadge}>{u.tenantName || '-'}</span></td>}
                      <td style={styles.td}>
                        <select style={styles.selectSmall} onChange={e => { if (e.target.value) handleAddUserToGroup(u.id, parseInt(e.target.value)); e.target.value = ''; }}>
                          <option value="">+ Grup Ekle</option>
                          {reportGroups.filter(g => !u.reportGroups?.find(ug => ug.id === g.id)).map(g => (
                            <option key={g.id} value={g.id}>{g.name} ({g.environment === 'test' ? '🧪 Test' : '🚀 Prod'})</option>
                          ))}
                        </select>
                        {u.reportGroups?.length > 0 && (
                          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {u.reportGroups.map(g => (
                              <span key={g.id} style={styles.tag}>{g.name}
                                <button onClick={() => handleRemoveUserFromGroup(u.id, g.id)} style={styles.tagRemove}>✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <button onClick={() => handleToggleCanAccessTest(u.id)} style={{ ...styles.primaryButton, backgroundColor: u.canAccessTest ? '#f59e0b' : '#6b7280', fontSize: '12px', padding: '4px 10px' }}>
                          {u.canAccessTest ? 'Aktif ✓' : 'Pasif'}
                        </button>
                      </td>
                      <td style={styles.td}>{u.isActive && <button onClick={() => handleDeactivateUser(u.id)} style={styles.dangerButton}>Deaktif Et</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination currentPage={userPage} total={totalPages(filteredUsers)} onPageChange={setUserPage} />
            </div>
          </div>
        )}

        {/* RAPOR GRUPLARI */}
        {activeTab === 'groups' && !loading && (
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Yeni Rapor Grubu</h3>
              <form onSubmit={handleCreateGroup} style={styles.form}>
                <input placeholder="Grup Adı" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} style={styles.input} required />
                <input placeholder="Açıklama (opsiyonel)" value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} style={styles.input} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={styles.label}>Ortam</label>
                  <select style={styles.input} value={newGroupEnv} onChange={e => setNewGroupEnv(e.target.value)}>
                    <option value="prod">🚀 Production</option>
                    <option value="test">🧪 Test</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={styles.label}>AI Chat</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setNewGroupAiChat(v => !v)}>
                    <div style={{ width: '40px', height: '22px', backgroundColor: newGroupAiChat ? '#1a1a2e' : '#d1d5db', borderRadius: '11px', position: 'relative', transition: 'background-color 0.2s' }}>
                      <div style={{ width: '18px', height: '18px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: newGroupAiChat ? '20px' : '2px', transition: 'left 0.2s' }} />
                    </div>
                    <span style={{ fontSize: '13px', color: '#374151' }}>{newGroupAiChat ? '🤖 Aktif' : 'Pasif'}</span>
                  </div>
                </div>
                {isSuperAdmin && (
                  <select style={styles.input} value={newGroupTenantId} onChange={e => setNewGroupTenantId(e.target.value)} required>
                    <option value="">Tenant Seçin...</option>
                    {tenants.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                <button type="submit" style={styles.primaryButton}>Oluştur</button>
              </form>
            </div>
            <div style={styles.card}>
              <div style={styles.tableHeader}>
                <h3 style={styles.cardTitle}>Rapor Grupları ({filteredGroups.length})</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {isSuperAdmin && (
                    <select
                      style={{ ...styles.input, minWidth: '200px' }}
                      value={groupTenantFilter}
                      onChange={e => { setGroupTenantFilter(e.target.value); setGroupPage(1); }}
                      required
                    >
                      <option value="">Tenant Seçin...</option>
                      {tenants.filter(t => t.isActive).map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                    </select>
                  )}
                  <select style={{ ...styles.input, minWidth: '140px' }} value={groupEnvFilter} onChange={e => { setGroupEnvFilter(e.target.value); setGroupPage(1); }}>
                    <option value="">Tüm Ortamlar</option>
                    <option value="prod">🚀 Production</option>
                    <option value="test">🧪 Test</option>
                  </select>
                  <input placeholder="🔍 Grup ara..." value={groupSearch} onChange={e => { setGroupSearch(e.target.value); setGroupPage(1); }} style={styles.searchInput} />
                </div>
              </div>

              {isSuperAdmin && !groupTenantFilter ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>Rapor gruplarını görmek için tenant seçin.</p>
              ) : (
                <>
                  {paginate(filteredGroups, groupPage).map(g => (
                    <div key={g.id} style={styles.groupCard}>
                      <div style={styles.groupHeader}>
                        <strong>{g.name}</strong>
                        {g.description && <span style={styles.groupDesc}>{g.description}</span>}
                        <EnvBadge env={g.environment} />
                        <span style={styles.badge}>{g.reports?.length || 0} rapor</span>
                        {isSuperAdmin && g.tenantName && <span style={styles.tenantBadge}>{g.tenantName}</span>}
                        {/* AI Chat inline toggle */}
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginLeft: 'auto' }}
                          onClick={async () => {
                            try {
                              await toggleAiChatForGroup(g.id);
                              setMessage('AI Chat durumu güncellendi.');
                              loadAll();
                            } catch { setMessage('İşlem başarısız.'); }
                          }}
                          title="AI Chat aktif/pasif"
                        >
                          <div style={{ width: '36px', height: '20px', backgroundColor: g.aiChatEnabled ? '#1a1a2e' : '#d1d5db', borderRadius: '10px', position: 'relative', transition: 'background-color 0.2s', flexShrink: 0 }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: g.aiChatEnabled ? '18px' : '2px', transition: 'left 0.2s' }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>{g.aiChatEnabled ? '🤖 AI Aktif' : 'AI Pasif'}</span>
                        </div>
                      </div>
                      <div style={styles.groupBody}>
                        <p style={styles.label}>Raporlar: {g.reports?.length > 1 && <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '11px' }}>(sıralamak için sürükleyin)</span>}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                          {g.reports?.map(r => {
                            const isDragging = dragReport?.groupId === g.id && dragReport?.reportId === r.id;
                            return (
                              <span
                                key={r.id}
                                draggable
                                onDragStart={() => setDragReport({ groupId: g.id, reportId: r.id })}
                                onDragEnd={() => setDragReport(null)}
                                onDragOver={e => { if (dragReport?.groupId === g.id) e.preventDefault(); }}
                                onDrop={e => { e.preventDefault(); handleReportDrop(g.id, r.id); }}
                                style={{
                                  ...styles.tag,
                                  cursor: 'grab',
                                  opacity: isDragging ? 0.4 : 1,
                                  border: dragReport?.groupId === g.id && !isDragging ? '1px dashed #94a3b8' : styles.tag.border,
                                }}
                                title="Sıralamak için sürükleyip bırakın"
                              >
                                <span style={{ marginRight: '4px', color: '#94a3b8', cursor: 'grab' }}>⠿</span>
                                {r.name}
                                <button onClick={() => handleRemoveReportFromGroup(g.id, r.id)} style={styles.tagRemove}>✕</button>
                              </span>
                            );
                          })}
                        </div>
                        <select
                          style={styles.selectSmall}
                          onChange={e => { if (e.target.value) handleAddReportToGroup(g.id, parseInt(e.target.value)); e.target.value = ''; }}
                        >
                          <option value="">+ Rapor Ekle</option>
                          {reports
                            .filter(r =>
                              r.environment === g.environment &&
                              !g.reports?.find(gr => gr.id === r.id) &&
                              (isSuperAdmin ? String(r.tenantId) === String(g.tenantId) : true)
                            )
                            .map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                      </div>
                    </div>
                  ))}
                  <Pagination currentPage={groupPage} total={totalPages(filteredGroups)} onPageChange={setGroupPage} />
                </>
              )}
            </div>
          </div>
        )}

        {/* RAPORLAR */}
        {activeTab === 'reports' && !loading && (
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Yeni Rapor Ekle</h3>
              <form onSubmit={handleCreateReport} style={styles.form}>
                {isSuperAdmin && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={styles.label}>Tenant</label>
                    <select style={styles.input} value={newReportTenantId} onChange={e => setNewReportTenantId(e.target.value)} required>
                      <option value="">Tenant Seçin...</option>
                      {tenants.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={styles.label}>Ortam</label>
                  <select style={styles.input} value={newReportEnv} onChange={e => setNewReportEnv(e.target.value)}>
                    <option value="prod">🚀 Production</option>
                    <option value="test">🧪 Test</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={styles.label}>Power BI Raporu</label>
                  {isSuperAdmin && !newReportTenantId ? (
                    <div style={{ ...styles.input, color: '#94a3b8', backgroundColor: '#f9fafb' }}>Önce tenant seçin</div>
                  ) : powerBiLoading ? (
                    <div style={{ ...styles.input, color: '#94a3b8', backgroundColor: '#f9fafb' }}>Yükleniyor...</div>
                  ) : (
                    <select style={{ ...styles.input, minWidth: '300px' }} value={newReportId} onChange={e => { const s = powerBiReports.find(r => r.id === e.target.value); if (s) { setNewReportId(s.id); setNewReportName(s.name); } }} required>
                      <option value="">{powerBiReports.length === 0 ? 'Bu ortamda rapor bulunamadı' : `Rapor Seçin... (${powerBiReports.filter(pbr => !reports.find(r => r.powerBiReportId === pbr.id)).length} yeni)`}</option>
                      {powerBiReports.filter(pbr => !reports.find(r => r.powerBiReportId === pbr.id)).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                </div>
                <input placeholder="Rapor Adı" value={newReportName} onChange={e => setNewReportName(e.target.value)} style={styles.input} required />
                <input placeholder="Açıklama (opsiyonel)" value={newReportDesc} onChange={e => setNewReportDesc(e.target.value)} style={styles.input} />
                <button type="submit" style={styles.primaryButton}>Ekle</button>
              </form>
            </div>
            <div style={styles.card}>
              <div style={styles.tableHeader}>
                <h3 style={styles.cardTitle}>Raporlar ({filteredReports.length})</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {isSuperAdmin && (
                    <select
                      style={{ ...styles.input, minWidth: '200px' }}
                      value={reportTenantFilter}
                      onChange={e => { setReportTenantFilter(e.target.value); setReportPage(1); }}
                    >
                      <option value="">Tenant Seçin...</option>
                      {tenants.filter(t => t.isActive).map(t => (
                        <option key={t.id} value={String(t.id)}>{t.name}</option>
                      ))}
                    </select>
                  )}
                  <select
                    style={{ ...styles.input, minWidth: '140px' }}
                    value={reportEnvFilter}
                    onChange={e => { setReportEnvFilter(e.target.value); setReportPage(1); }}
                  >
                    <option value="">Tüm Ortamlar</option>
                    <option value="prod">🚀 Production</option>
                    <option value="test">🧪 Test</option>
                  </select>
                  <input
                    placeholder="🔍 Rapor ara..."
                    value={reportSearch}
                    onChange={e => { setReportSearch(e.target.value); setReportPage(1); }}
                    style={styles.searchInput}
                  />
                </div>
              </div>
              {isSuperAdmin && !reportTenantFilter ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>Raporları görmek için tenant seçin.</p>
              ) : (
                <>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Rapor Adı</th>
                        <th style={styles.th}>Ortam</th>
                        <th style={styles.th}>Açıklama</th>
                        <th style={styles.th}>Durum</th>
                        {isSuperAdmin && <th style={styles.th}>Tenant</th>}
                        <th style={styles.th}>AI Model</th>
                        <th style={styles.th}>Veri</th>
                      </tr>
                    </thead>
                    <tbody>
                    {paginate(filteredReports, reportPage).map(r => (
                      <tr key={r.id}>
                        <td style={styles.td}>{r.name}</td>
                        <td style={styles.td}><EnvBadge env={r.environment} /></td>
                        <td style={styles.td}>{r.description || '-'}</td>
                        <td style={styles.td}>
                          <span style={{ color: r.isActive ? 'green' : 'red' }}>{r.isActive ? 'Aktif' : 'Pasif'}</span>
                        </td>
                        {isSuperAdmin && (
                          <td style={styles.td}><span style={styles.tenantBadge}>{r.tenantName || '-'}</span></td>
                        )}
                        <td style={styles.td}>
                          <select style={styles.selectSmall} value={r.reportContextId || ''} onChange={e => handleAssignContext(r.id, e.target.value || 0)}>
                            <option value="">Model Seç...</option>
                            {reportContexts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                          {r.reportContextName && (
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>✓ {r.reportContextName}</div>
                          )}
                        </td>
                        <td style={styles.td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <button
                            style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px', backgroundColor: '#0ea5e9', opacity: refreshingIds[r.id] ? 0.6 : 1 }}
                            disabled={refreshingIds[r.id]}
                            onClick={async () => {
                              setRefreshingIds(prev => ({ ...prev, [r.id]: true }));
                              try {
                                await refreshReportDataset(r.id);
                                setMessage(`✅ "${r.name}" dataset refresh başlatıldı.`);
                                setRefreshStatuses(prev => ({ ...prev, [r.id]: 'Unknown' }));
                              } catch (err) {
                                setMessage(err?.response?.data?.message || '❌ Refresh başarısız.');
                              } finally {
                                setRefreshingIds(prev => ({ ...prev, [r.id]: false }));
                              }
                            }}
                          >
                            {refreshingIds[r.id] ? '⏳' : '🔄'} Yenile
                          </button>

                          <button
                            style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px', backgroundColor: '#6b7280', opacity: refreshingIds[`status_${r.id}`] ? 0.6 : 1 }}
                            disabled={refreshingIds[`status_${r.id}`]}
                            onClick={async () => {
                              setRefreshingIds(prev => ({ ...prev, [`status_${r.id}`]: true }));
                              try {
                                const res = await getRefreshStatus(r.id);
                                setRefreshStatuses(prev => ({ ...prev, [r.id]: res.status }));
                              } catch (err) {
                                setMessage('❌ Durum sorgulanamadı.');
                              } finally {
                                setRefreshingIds(prev => ({ ...prev, [`status_${r.id}`]: false }));
                              }
                            }}
                          >
                            {refreshingIds[`status_${r.id}`] ? '⏳' : '📊'} Durum
                          </button>

                          {refreshStatuses[r.id] && (
                          <span style={{
                            fontSize: '11px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px',
                            backgroundColor:
                              refreshStatuses[r.id] === 'Completed' ? '#dcfce7' :
                              refreshStatuses[r.id] === 'Failed' ? '#fef2f2' :
                              refreshStatuses[r.id] === 'NoHistory' ? '#f1f5f9' : '#fef9c3',
                            color:
                              refreshStatuses[r.id] === 'Completed' ? '#166534' :
                              refreshStatuses[r.id] === 'Failed' ? '#991b1b' :
                              refreshStatuses[r.id] === 'NoHistory' ? '#64748b' : '#854d0e'
                          }}>
                            {refreshStatuses[r.id] === 'Completed' ? '✅ Tamamlandı' :
                            refreshStatuses[r.id] === 'Failed' ? '❌ Hata' :
                            refreshStatuses[r.id] === 'NoHistory' ? '📭 Geçmiş yok' : '⏳ Devam ediyor'}
                          </span>
                        )}
                        </div>
                      </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                  <Pagination currentPage={reportPage} total={totalPages(filteredReports)} onPageChange={setReportPage} />
                </>
              )}
            </div>
          </div>
        )}

        {/* AI MODELLER */}
        {activeTab === 'contexts' && !loading && (
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>{editingContext ? 'Context Düzenle' : 'Yeni AI Model Tanımı'}</h3>
              <form onSubmit={handleCreateContext} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input placeholder="Model Adı" value={newContextName} onChange={e => setNewContextName(e.target.value)} style={styles.input} required />
                <div><p style={{ ...styles.label, marginBottom: '6px' }}>Dimension Tanımları</p>
                  <textarea value={newContextDimensions} onChange={e => setNewContextDimensions(e.target.value)} style={{ ...styles.input, width: '100%', minHeight: '120px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }} required /></div>
                <div><p style={{ ...styles.label, marginBottom: '6px' }}>Measure Tanımları</p>
                  <textarea value={newContextMeasures} onChange={e => setNewContextMeasures(e.target.value)} style={{ ...styles.input, width: '100%', minHeight: '120px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }} required /></div>
                <div><p style={{ ...styles.label, marginBottom: '6px' }}>AI Kuralları (opsiyonel)</p>
                  <textarea value={newContextRules} onChange={e => setNewContextRules(e.target.value)} style={{ ...styles.input, width: '100%', minHeight: '100px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }} /></div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" style={styles.primaryButton}>{editingContext ? 'Güncelle' : 'Oluştur'}</button>
                  {editingContext && <button type="button" style={styles.dangerButton} onClick={() => { setEditingContext(null); setNewContextName(''); setNewContextDimensions(''); setNewContextMeasures(''); setNewContextRules(''); }}>İptal</button>}
                </div>
              </form>
            </div>
            <div style={styles.card}>
              <div style={styles.tableHeader}>
                <h3 style={styles.cardTitle}>AI Modeller ({filteredContexts.length})</h3>
                <input placeholder="🔍 Model ara..." value={contextSearch} onChange={e => { setContextSearch(e.target.value); setContextPage(1); }} style={styles.searchInput} />
              </div>
              {paginate(filteredContexts, contextPage).map(c => (
                <div key={c.id} style={styles.groupCard}>
                  <div style={styles.groupHeader}>
                    <strong>{c.name}</strong><span style={styles.badge}>{c.reportCount} rapor</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleEditContext(c)} style={styles.primaryButton}>Düzenle</button>
                      <button onClick={() => handleDeleteContext(c.id)} style={styles.dangerButton}>Sil</button>
                    </div>
                  </div>
                  <div style={styles.groupBody}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div><p style={styles.label}>Dimension Tanımları:</p><pre style={{ margin: 0, fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap', backgroundColor: '#f8fafc', padding: '8px', borderRadius: '4px' }}>{c.dimensionDefinitions}</pre></div>
                      <div><p style={styles.label}>Measure Tanımları:</p><pre style={{ margin: 0, fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap', backgroundColor: '#f8fafc', padding: '8px', borderRadius: '4px' }}>{c.measureDefinitions}</pre></div>
                      {c.systemPromptRules && <div style={{ gridColumn: '1 / -1' }}><p style={styles.label}>AI Kuralları:</p><pre style={{ margin: 0, fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap', backgroundColor: '#fff3cd', padding: '8px', borderRadius: '4px' }}>{c.systemPromptRules}</pre></div>}
                    </div>
                  </div>
                </div>
              ))}
              <Pagination currentPage={contextPage} total={totalPages(filteredContexts)} onPageChange={setContextPage} />
            </div>
          </div>
        )}

        {/* AI FEEDBACKS */}
        {activeTab === 'feedbacks' && !loading && (
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={styles.card}>
                <div style={styles.tableHeader}>
                  <h3 style={styles.cardTitle}>AI Feedbacks ({filteredFeedbacks.length})</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input placeholder="🔍 Ara..." value={feedbackSearch} onChange={e => { setFeedbackSearch(e.target.value); setFeedbackPage(1); }} style={styles.searchInput} />
                    <button onClick={loadFeedbacks} style={styles.primaryButton}>Yenile</button>
                  </div>
                </div>
                {filteredFeedbacks.length === 0 ? (
                  <p style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>Henüz feedback yok.</p>
                ) : paginate(filteredFeedbacks, feedbackPage).map(f => (
                  <div key={f.id} onClick={() => setSelectedFeedback(f)} style={{ ...styles.feedbackItem, ...(selectedFeedback?.id === f.id ? styles.feedbackItemActive : {}) }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{f.userEmail}</span>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(f.createdAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: '500', marginBottom: '4px' }}>{f.userMessage?.substring(0, 80)}{f.userMessage?.length > 80 ? '...' : ''}</div>
                    {f.userComment && <div style={{ fontSize: '12px', color: '#ef4444' }}>💬 {f.userComment}</div>}
                  </div>
                ))}
                <Pagination currentPage={feedbackPage} total={totalPages(filteredFeedbacks)} onPageChange={setFeedbackPage} />
              </div>
            </div>
            {selectedFeedback && (
              <div style={{ width: '480px' }}>
                <div style={styles.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={styles.cardTitle}>Detay</h3>
                    <button onClick={() => setSelectedFeedback(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
                  </div>
                  <div style={styles.feedbackDetail}>
                    <div style={styles.detailRow}><span style={styles.detailLabel}>Kullanıcı</span><span style={styles.detailValue}>{selectedFeedback.userEmail}</span></div>
                    <div style={styles.detailRow}><span style={styles.detailLabel}>Tarih</span><span style={styles.detailValue}>{new Date(selectedFeedback.createdAt).toLocaleString('tr-TR')}</span></div>
                    <div style={styles.detailRow}><span style={styles.detailLabel}>Rapor ID</span><span style={styles.detailValue}>{selectedFeedback.reportId || '-'}</span></div>
                  </div>
                  <div style={styles.detailSection}><p style={styles.detailSectionTitle}>👤 Kullanıcı Sorusu</p><div style={styles.detailBox}>{selectedFeedback.userMessage}</div></div>
                  <div style={styles.detailSection}><p style={styles.detailSectionTitle}>🤖 AI Cevabı</p><div style={styles.detailBox}>{selectedFeedback.aiResponse}</div></div>
                  {selectedFeedback.userComment && <div style={styles.detailSection}><p style={styles.detailSectionTitle}>💬 Kullanıcı Yorumu</p><div style={{ ...styles.detailBox, backgroundColor: '#fef2f2', borderColor: '#fecaca' }}>{selectedFeedback.userComment}</div></div>}
                  {selectedFeedback.activeFilters && <div style={styles.detailSection}><p style={styles.detailSectionTitle}>🔍 Aktif Filtreler</p><div style={styles.detailBox}>{selectedFeedback.activeFilters}</div></div>}
                  {selectedFeedback.conversationHistory && (
                    <div style={styles.detailSection}>
                      <p style={styles.detailSectionTitle}>📜 Conversation History</p>
                      <div style={{ ...styles.detailBox, maxHeight: '200px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '11px' }}>
                        {(() => { try { return JSON.parse(selectedFeedback.conversationHistory).map((h, i) => (<div key={i} style={{ marginBottom: '8px', padding: '4px', backgroundColor: h.role === 'user' ? '#eff6ff' : '#f0fdf4', borderRadius: '4px' }}><strong>{h.role === 'user' ? '👤' : '🤖'}</strong> {h.content?.substring(0, 200)}{h.content?.length > 200 ? '...' : ''}</div>)); } catch { return <span>{selectedFeedback.conversationHistory}</span>; } })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* MASTER DATA */}
        {activeTab === 'masterdata' && !loading && (
          <div>
            {isSuperAdmin && (
              <div style={styles.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ ...styles.label, margin: 0, fontWeight: '600' }}>Tenant Seç:</label>
                  <select style={{ ...styles.input, minWidth: '250px' }} value={mdTenantId} onChange={e => setMdTenantId(e.target.value)}>
                    <option value="">Tenant Seçin...</option>
                    {tenants.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name} ({t.schemaName || 'schema yok'})</option>)}
                  </select>
                  {mdTenantId && <button style={styles.primaryButton} onClick={() => loadMasterData(mdTenantId)}>🔄 Yenile</button>}
                </div>
                {!mdTenantId && <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0 0 0' }}>Master data yönetmek için tenant seçin.</p>}
              </div>
            )}
            {(!isSuperAdmin || mdTenantId) && (
              <>
                {(() => {
                  const mdNavGroups = [
                    { standalone: true, key: 'salesforce', label: '📥 Toplu Yükleme' },
                    {
                      label: '👥 Satış Ekibi Yönetimi',
                      items: [
                        { key: 'teams', label: `👥 Ekipler (${teams.length})` },
                        { key: 'regions', label: `🗺️ Bölgeler (${regions.length})` },
                        { key: 'reps', label: `👤 Temsilciler (${reps.length})` },
                        { key: 'bricks', label: `📍 Brick Atamaları (${repBricks.length})` },
                      ],
                    },
                    {
                      label: '🏪 Ürün & Pazar Yönetimi',
                      items: [
                        { key: 'markets', label: `🏪 Pazarlar (${markets.length})` },
                        { key: 'products', label: `💊 Ürünler (${products.length})` },
                        { key: 'product-targets', label: '🎯 Ürün Hedef Tanımları' },
                      ],
                    },
                  ];
                  const activeGroup = mdNavGroups.find(g => !g.standalone && g.items.some(it => it.key === mdSubTab));
                  return (
                    <>
                      <div style={{ ...styles.tabs, marginBottom: '0', backgroundColor: '#f8fafc', borderRadius: activeGroup ? '8px 8px 0 0' : '8px' }}>
                        {mdNavGroups.map(g =>
                          g.standalone ? (
                            <button
                              key={g.key}
                              style={{ ...styles.tab, ...(mdSubTab === g.key ? styles.tabActive : {}) }}
                              onClick={() => setMdSubTab(g.key)}
                            >
                              {g.label}
                            </button>
                          ) : (
                            <button
                              key={g.label}
                              style={{ ...styles.tab, ...(activeGroup === g ? styles.tabActive : {}) }}
                              onClick={() => setMdSubTab(g.items[0].key)}
                            >
                              {g.label}
                            </button>
                          )
                        )}
                      </div>
                      {activeGroup && (
                        <div style={{ display: 'flex', backgroundColor: '#eef2f7', borderRadius: '0 0 8px 8px', marginBottom: '16px', padding: '4px 8px', gap: '4px' }}>
                          {activeGroup.items.map(it => (
                            <button
                              key={it.key}
                              style={{ ...styles.tab, fontSize: '13px', padding: '8px 14px', ...(mdSubTab === it.key ? styles.tabActive : {}) }}
                              onClick={() => setMdSubTab(it.key)}
                            >
                              {it.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {!activeGroup && <div style={{ marginBottom: '16px' }} />}
                    </>
                  );
                })()}
                {mdLoading && <p style={{ color: '#94a3b8', padding: '16px' }}>Yükleniyor...</p>}
                {!mdLoading && (
                  <>
                    {mdSubTab === 'salesforce' && (
                      <div>
                        <div style={styles.card}>
                          <h3 style={styles.cardTitle}>📥 Saha Ekibi Toplu Yükleme</h3>
                          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                            Excel formatı: <strong>{SF_LABEL_KEYS.map(k => sf(k)).join(', ')}</strong>
                          </p>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                            <input type="file" accept=".xlsx" onChange={e => { setBulkFile(e.target.files[0]); setBulkErrors([]); setBulkResult(null); }} style={{ fontSize: '14px' }} />
                            <button style={{ ...styles.primaryButton, opacity: bulkImporting || !bulkFile ? 0.6 : 1 }} disabled={bulkImporting || !bulkFile}
                              onClick={async () => {
                                if (!bulkFile) return;
                                setBulkImporting(true); setBulkErrors([]); setBulkResult(null);
                                try {
                                  const result = await bulkImportSalesForce(bulkFile, isSuperAdmin ? mdTenantId : null);
                                  setBulkResult(result); setMessage(`✅ ${result.insertedCount} kayıt eklendi, ${result.closedCount} kayıt kapatıldı.`);
                                  loadMasterData(isSuperAdmin ? mdTenantId : null);
                                } catch (err) { setBulkErrors(err.response?.data?.errors || []); setMessage('❌ Validasyon hataları bulundu.'); }
                                finally { setBulkImporting(false); }
                              }}>
                              {bulkImporting ? 'Yükleniyor...' : '📤 Yükle'}
                            </button>
                          </div>
                          {bulkResult && <div style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px' }}><strong style={{ color: '#166534' }}>✅ Import tamamlandı</strong><p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#166534' }}>{bulkResult.insertedCount} kayıt eklendi, {bulkResult.closedCount} kayıt kapatıldı.</p></div>}
                          {bulkErrors.length > 0 && (
                            <div>
                              <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px 16px', marginBottom: '12px' }}><strong style={{ color: '#991b1b' }}>❌ {bulkErrors.length} hata bulundu</strong></div>
                              <table style={styles.table}><thead><tr><th style={styles.th}>Satır</th><th style={styles.th}>Kolon</th><th style={styles.th}>Hata</th></tr></thead>
                                <tbody>{bulkErrors.map((err, i) => <tr key={i}><td style={{ ...styles.td, color: '#ef4444', fontWeight: '600' }}>{err.rowNumber === 0 ? 'Genel' : `Satır ${err.rowNumber}`}</td><td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '12px' }}>{err.column}</td><td style={styles.td}>{err.message}</td></tr>)}</tbody>
                              </table>
                            </div>
                          )}
                        </div>
                        <div style={styles.card}>
                          <h3 style={styles.cardTitle}>💊 Ürün & Pazar Toplu Yükleme</h3>
                          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                            Excel formatı: <strong>{PM_LABEL_KEYS.map(k => pm(k)).join(' | ')}</strong>
                          </p>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
                            <input type="file" accept=".xlsx" onChange={e => { setPmFile(e.target.files[0] || null); setPmResult(null); setPmErrors([]); }} style={{ fontSize: '14px' }} />
                            <button style={{ ...styles.primaryButton, opacity: pmLoading || !pmFile ? 0.6 : 1 }} disabled={pmLoading || !pmFile} onClick={handleBulkImportProductsMarkets}>{pmLoading ? 'Yükleniyor...' : '📤 Yükle'}</button>
                          </div>
                          {pmResult && <div style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px' }}><strong style={{ color: '#166534' }}>✅ İçe aktarma tamamlandı</strong><ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#166534', fontSize: '13px' }}><li>Oluşturulan pazar: <strong>{pmResult.marketsCreated}</strong></li><li>Eklenen ürün: <strong>{pmResult.productsInserted}</strong></li><li>Güncellenen ürün: <strong>{pmResult.productsUpdated}</strong></li><li>Kurulan ürün-pazar ilişkisi: <strong>{pmResult.relationsCreated}</strong></li>{pmResult.targetsCreated > 0 && <li>Oluşturulan ürün hedefi: <strong>{pmResult.targetsCreated}</strong></li>}</ul></div>}
                          {pmErrors.length > 0 && <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px 16px' }}><strong style={{ color: '#991b1b' }}>❌ {pmErrors.length} hata bulundu</strong><ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#991b1b' }}>{pmErrors.map((e, i) => <li key={i}>{e}</li>)}</ul></div>}
                        </div>
                      </div>
                    )}
                    {mdSubTab === 'teams' && (
                      <div>
                        <div style={styles.card}>
                          <h3 style={styles.cardTitle}>Yeni Ekip Ekle</h3>
                          <div style={styles.form}>
                            <input placeholder="Ekip Adı (örn: BEYAZ)" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} style={styles.input} />
                            <button style={styles.primaryButton} onClick={async () => { if (!newTeamName.trim()) return; try { await createTeam(newTeamName.trim(), isSuperAdmin ? mdTenantId : user?.tenantId); setNewTeamName(''); setMessage('Ekip oluşturuldu.'); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch { setMessage('İşlem başarısız.'); } }}>Ekle</button>
                          </div>
                        </div>
                        <div style={styles.card}>
                          <h3 style={styles.cardTitle}>Ekipler ({teams.length})</h3>
                          <table style={styles.table}><thead><tr><th style={styles.th}>Ekip Adı</th><th style={styles.th}>Oluşturma</th><th style={styles.th}>İşlem</th></tr></thead>
                            <tbody>{teams.map(t => <tr key={t.id}><td style={styles.td}><strong>{t.teamName}</strong></td><td style={styles.td}>{new Date(t.createdAt).toLocaleDateString('tr-TR')}</td><td style={styles.td}><button style={{ ...styles.dangerButton, opacity: 0.4, cursor: 'not-allowed' }} disabled title={DELETE_DISABLED_HINT}>Sil</button></td></tr>)}</tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  {mdSubTab === 'regions' && (() => {
                    const filteredRegions = regions
                      .filter(r => !regionTeamFilter || String(r.teamId) === String(regionTeamFilter))
                      .sort((a, b) => {
                        const dir = regionSort.dir === 'asc' ? 1 : -1;
                        if (regionSort.col === 'teamName') return dir * a.teamName.localeCompare(b.teamName, 'tr');
                        if (regionSort.col === 'regionName') return dir * a.regionName.localeCompare(b.regionName, 'tr');
                        return 0;
                      });

                    const SortTh = ({ col, label }) => (
                      <th
                        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setRegionSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })}
                      >
                        {label}{regionSort.col === col ? (regionSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </th>
                    );

                    return (
                      <div>
                        <div style={styles.card}>
                          <h3 style={styles.cardTitle}>Yeni Bölge Ekle</h3>
                          <div style={styles.form}>
                            <select style={styles.input} value={newRegionTeamId} onChange={e => setNewRegionTeamId(e.target.value)}>
                              <option value="">Ekip Seçin...</option>
                              {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
                            </select>
                            <input placeholder="Bölge Adı (örn: ANKARA)" value={newRegionName} onChange={e => setNewRegionName(e.target.value)} style={styles.input} />
                            <button style={styles.primaryButton} onClick={async () => {
                              if (!newRegionName.trim() || !newRegionTeamId) return;
                              try {
                                await createRegion(newRegionName.trim(), parseInt(newRegionTeamId), isSuperAdmin ? mdTenantId : user?.tenantId);
                                setNewRegionName('');
                                setMessage('Bölge oluşturuldu.');
                                loadMasterData(isSuperAdmin ? mdTenantId : null);
                              } catch { setMessage('İşlem başarısız.'); }
                            }}>Ekle</button>
                          </div>
                        </div>
                        <div style={styles.card}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                            <h3 style={{ ...styles.cardTitle, margin: 0 }}>Bölgeler ({filteredRegions.length})</h3>
                            <select
                              style={{ ...styles.input, minWidth: '160px' }}
                              value={regionTeamFilter}
                              onChange={e => setRegionTeamFilter(e.target.value)}
                            >
                              <option value="">Tüm Ekipler</option>
                              {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
                            </select>
                          </div>
                          <table style={styles.table}>
                            <thead>
                              <tr>
                                <SortTh col="regionName" label="Bölge Adı" />
                                <SortTh col="teamName" label="Ekip" />
                                <th style={styles.th}>İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredRegions.map(r => (
                                <tr key={r.id}>
                                  <td style={styles.td}><strong>{r.regionName}</strong></td>
                                  <td style={styles.td}><span style={styles.tenantBadge}>{r.teamName}</span></td>
                                  <td style={styles.td}>
                                    <button
                                      style={{ ...styles.dangerButton, opacity: 0.4, cursor: 'not-allowed' }}
                                      disabled
                                      title={DELETE_DISABLED_HINT}
                                    >Sil</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {mdSubTab === 'reps' && (() => {
                  const filteredReps = reps
                    .filter(r => {
                      if (repTeamFilter && r.teamName !== repTeamFilter) return false;
                      if (repRegionFilter && r.regionName !== repRegionFilter) return false;
                      return true;
                    })
                    .sort((a, b) => {
                      const dir = repSort.dir === 'asc' ? 1 : -1;
                      if (repSort.col === 'repName') return dir * (a.repName || '').localeCompare(b.repName || '', 'tr');
                      if (repSort.col === 'repCode') return dir * (a.repCode || '').localeCompare(b.repCode || '', 'tr');
                      if (repSort.col === 'regionName') return dir * (a.regionName || '').localeCompare(b.regionName || '', 'tr');
                      if (repSort.col === 'teamName') return dir * (a.teamName || '').localeCompare(b.teamName || '', 'tr');
                      return 0;
                    });

                    const filteredRegionsForRep = repTeamFilter
                      ? regions.filter(r => r.teamName === repTeamFilter)
                      : regions;

                    const SortTh = ({ col, label }) => (
                      <th
                        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setRepSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })}
                      >
                        {label}{repSort.col === col ? (repSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                      </th>
                    );

                    return (
                      <div>
                        <div style={styles.card}>
                          <h3 style={styles.cardTitle}>Yeni Temsilci Ekle</h3>
                          <div style={styles.form}>
                            <select style={styles.input} value={newRepRegionId} onChange={e => setNewRepRegionId(e.target.value)}>
                              <option value="">Bölge Seçin...</option>
                              {regions.map(r => <option key={r.id} value={r.id}>{r.teamName} → {r.regionName}</option>)}
                            </select>
                            <input placeholder="Temsilci Adı" value={newRepName} onChange={e => setNewRepName(e.target.value)} style={styles.input} />
                            <input placeholder="Temsilci Kodu (opsiyonel)" value={newRepCode} onChange={e => setNewRepCode(e.target.value)} style={{ ...styles.input, minWidth: '160px' }} />
                            <button style={styles.primaryButton} onClick={async () => {
                              if (!newRepName.trim() || !newRepRegionId) return;
                              try {
                                await createRep(newRepName.trim(), newRepCode.trim() || null, parseInt(newRepRegionId), isSuperAdmin ? mdTenantId : user?.tenantId);
                                setNewRepName(''); setNewRepCode('');
                                setMessage('Temsilci oluşturuldu.');
                                loadMasterData(isSuperAdmin ? mdTenantId : null);
                              } catch { setMessage('İşlem başarısız.'); }
                            }}>Ekle</button>
                          </div>
                        </div>
                        <div style={styles.card}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                            <h3 style={{ ...styles.cardTitle, margin: 0 }}>Temsilciler ({filteredReps.length} / {reps.length})</h3>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <select
                                style={{ ...styles.input, minWidth: '150px' }}
                                value={repTeamFilter}
                                onChange={e => { setRepTeamFilter(e.target.value); setRepRegionFilter(''); }}
                              >
                                <option value="">Tüm Ekipler</option>
                                {teams.map(t => <option key={t.id} value={t.teamName}>{t.teamName}</option>)}
                              </select>

                              <select
                                style={{ ...styles.input, minWidth: '160px' }}
                                value={repRegionFilter}
                                onChange={e => setRepRegionFilter(e.target.value)}
                              >
                                <option value="">Tüm Bölgeler</option>
                                {filteredRegionsForRep.map(r => <option key={r.id} value={r.regionName}>{r.regionName}</option>)}
                              </select>
                            </div>
                          </div>
                          <table style={styles.table}>
                            <thead>
                              <tr>
                                <SortTh col="repName" label="Temsilci" />
                                <SortTh col="repCode" label="Kod" />
                                <SortTh col="regionName" label="Bölge" />
                                <SortTh col="teamName" label="Ekip" />
                                <th style={styles.th}>İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredReps.map(r => (
                                <tr key={r.id}>
                                  {editingRep?.id === r.id ? (
                                    <>
                                      <td style={styles.td}>
                                        <input
                                          value={editingRep.repName}
                                          onChange={e => setEditingRep(v => ({ ...v, repName: e.target.value }))}
                                          style={{ ...styles.input, minWidth: '160px', padding: '4px 8px' }}
                                          autoFocus
                                        />
                                      </td>
                                      <td style={styles.td}>
                                        <input
                                          value={editingRep.repCode}
                                          onChange={e => setEditingRep(v => ({ ...v, repCode: e.target.value }))}
                                          style={{ ...styles.input, minWidth: '110px', padding: '4px 8px' }}
                                          placeholder="Kod"
                                        />
                                      </td>
                                      <td style={styles.td} colSpan={2}>
                                        <select
                                          value={editingRep.regionId}
                                          onChange={e => setEditingRep(v => ({ ...v, regionId: e.target.value }))}
                                          style={styles.selectSmall}
                                        >
                                          {regions.map(rg => (
                                            <option key={rg.id} value={rg.id}>{rg.teamName} → {rg.regionName}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td style={styles.td}>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                          <button
                                            style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }}
                                            onClick={async () => {
                                              if (!editingRep.repName.trim()) { setMessage('Temsilci adı zorunlu.'); return; }
                                              try {
                                                await updateRep(
                                                  editingRep.id, editingRep.repName.trim(),
                                                  editingRep.repCode.trim() || null,
                                                  parseInt(editingRep.regionId, 10),
                                                  isSuperAdmin ? mdTenantId : user?.tenantId
                                                );
                                                setEditingRep(null);
                                                setMessage('Temsilci güncellendi.');
                                                loadMasterData(isSuperAdmin ? mdTenantId : null);
                                              } catch (err) { setMessage(apiErrorMessage(err)); }
                                            }}
                                          >Kaydet</button>
                                          <button style={styles.dangerButton} onClick={() => setEditingRep(null)}>İptal</button>
                                        </div>
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td style={styles.td}><strong>{r.repName}</strong></td>
                                      <td style={styles.td}><span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{r.repCode || '-'}</span></td>
                                      <td style={styles.td}>{r.regionName}</td>
                                      <td style={styles.td}><span style={styles.tenantBadge}>{r.teamName}</span></td>
                                      <td style={styles.td}>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                          <button
                                            style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }}
                                            onClick={() => setEditingRep({ id: r.id, repName: r.repName, repCode: r.repCode || '', regionId: r.regionId })}
                                          >Düzenle</button>
                                          <button
                                            style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px', backgroundColor: '#166534' }}
                                            onClick={() => setHandoverRep(r)}
                                          >Devret</button>
                                          <button style={styles.dangerButton} onClick={() => setRetiringRep(r)}>Sil</button>
                                        </div>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                    {mdSubTab === 'bricks' && (
                      <BrickAssignmentPanel repBricks={repBricks} teams={teams} regions={regions} reps={reps}
                        tid={isSuperAdmin ? mdTenantId : null} isSuperAdmin={isSuperAdmin}
                        mdTenantId={mdTenantId} loadMasterData={loadMasterData} setMessage={setMessage} styles={styles} />
                    )}
                    {mdSubTab === 'markets' && (() => {
                      const filteredMarkets = markets
                        .filter(m => !marketTeamFilter || String(m.teamId) === String(marketTeamFilter))
                        .sort((a, b) => {
                          const dir = marketSort.dir === 'asc' ? 1 : -1;
                          if (marketSort.col === 'marketName') return dir * a.marketName.localeCompare(b.marketName, 'tr');
                          if (marketSort.col === 'teamName') return dir * a.teamName.localeCompare(b.teamName, 'tr');
                          return 0;
                        });
                      const SortTh = ({ col, label }) => (
                        <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => setMarketSort(s => s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })}>
                          {label}{marketSort.col === col ? (marketSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                        </th>
                      );
                      return (
                        <div>
                          <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Yeni Pazar Ekle</h3>
                            <div style={styles.form}>
                              <select style={styles.input} value={newMarketTeamId} onChange={e => setNewMarketTeamId(e.target.value)}><option value="">Ekip Seçin...</option>{teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}</select>
                              <input placeholder="Pazar Adı (örn: PLAVIX)" value={newMarketName} onChange={e => setNewMarketName(e.target.value)} style={styles.input} />
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b', cursor: 'pointer' }}><input type="checkbox" checked={newMarketWeekly} onChange={e => setNewMarketWeekly(e.target.checked)} />Haftalık</label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b', cursor: 'pointer' }}><input type="checkbox" checked={newMarketMonthly} onChange={e => setNewMarketMonthly(e.target.checked)} />Aylık</label>
                              <button style={styles.primaryButton} onClick={async () => { if (!newMarketName.trim() || !newMarketTeamId) return; try { await createMarket(newMarketName.trim(), parseInt(newMarketTeamId), isSuperAdmin ? mdTenantId : user?.tenantId, newMarketWeekly, newMarketMonthly); setNewMarketName(''); setNewMarketWeekly(true); setNewMarketMonthly(true); setMessage('Pazar oluşturuldu.'); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch { setMessage('İşlem başarısız.'); } }}>Ekle</button>
                            </div>
                          </div>
                          <div style={styles.card}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                              <h3 style={{ ...styles.cardTitle, margin: 0 }}>Pazarlar ({filteredMarkets.length})</h3>
                              <select style={{ ...styles.input, minWidth: '160px' }} value={marketTeamFilter} onChange={e => setMarketTeamFilter(e.target.value)}><option value="">Tüm Ekipler</option>{teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}</select>
                            </div>
                            <table style={styles.table}>
                              <thead><tr><SortTh col="marketName" label="Pazar Adı" /><SortTh col="teamName" label="Ekip" /><th style={{ ...styles.th, textAlign: 'center' }}>Haftalık</th><th style={{ ...styles.th, textAlign: 'center' }}>Aylık</th><th style={styles.th}>İşlem</th></tr></thead>
                              <tbody>
                                {filteredMarkets.map(m => (
                                  <tr key={m.id}>
                                    {editingMarket?.id === m.id ? (
                                      <>
                                        <td style={styles.td}><input value={editingMarket.marketName} onChange={e => setEditingMarket(v => ({ ...v, marketName: e.target.value }))} style={{ ...styles.input, minWidth: '180px', padding: '4px 8px' }} autoFocus /></td>
                                        <td style={styles.td}><select value={editingMarket.teamId} onChange={e => setEditingMarket(v => ({ ...v, teamId: e.target.value }))} style={styles.selectSmall}>{teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}</select></td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={!!editingMarket.showInWeeklyDashboard} onChange={e => setEditingMarket(v => ({ ...v, showInWeeklyDashboard: e.target.checked }))} /></td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={!!editingMarket.showInMonthlyDashboard} onChange={e => setEditingMarket(v => ({ ...v, showInMonthlyDashboard: e.target.checked }))} /></td>
                                        <td style={styles.td}><div style={{ display: 'flex', gap: '6px' }}><button style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }} onClick={async () => { if (!editingMarket.marketName.trim()) return; try { await updateMarket(editingMarket.id, editingMarket.marketName.trim(), parseInt(editingMarket.teamId), isSuperAdmin ? mdTenantId : user?.tenantId, editingMarket.showInWeeklyDashboard, editingMarket.showInMonthlyDashboard); setEditingMarket(null); setMessage('Pazar güncellendi.'); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch { setMessage('İşlem başarısız.'); } }}>Kaydet</button><button style={styles.dangerButton} onClick={() => setEditingMarket(null)}>İptal</button></div></td>
                                      </>
                                    ) : (
                                      <>
                                        <td style={styles.td}><strong>{m.marketName}</strong></td>
                                        <td style={styles.td}><span style={styles.tenantBadge}>{m.teamName}</span></td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{m.showInWeeklyDashboard ? '✓' : '—'}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{m.showInMonthlyDashboard ? '✓' : '—'}</td>
                                        <td style={styles.td}><div style={{ display: 'flex', gap: '6px' }}><button style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }} onClick={() => setEditingMarket({ id: m.id, marketName: m.marketName, teamId: m.teamId, showInWeeklyDashboard: m.showInWeeklyDashboard, showInMonthlyDashboard: m.showInMonthlyDashboard })}>Düzenle</button><button style={styles.dangerButton} onClick={async () => { if (!window.confirm(`"${m.marketName}" pazarını silmek istiyor musunuz? Bu pazarın ürün eşleşmeleri de kaldırılacak.`)) return; try { const res = await deleteMarket(m.id, isSuperAdmin ? mdTenantId : null); setMessage(res?.message || 'Pazar silindi.'); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch (err) { setMessage(apiErrorMessage(err)); } }}>Sil</button></div></td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                    {mdSubTab === 'products' && (() => {
                      const filteredProducts = products.filter(p => {
                        if (productTeamFilter) { const pmn = (p.markets || '').split(', ').map(s => s.trim()).filter(Boolean); const tm = markets.filter(m => String(m.teamId) === String(productTeamFilter)).map(m => m.marketName); if (!pmn.some(pm => tm.includes(pm))) return false; }
                        if (productMarketFilter) { const pmn = (p.markets || '').split(', ').map(s => s.trim()).filter(Boolean); if (!pmn.includes(productMarketFilter)) return false; }
                        if (productSearch) { const q = productSearch.toLowerCase(); return p.imsProductName?.toLowerCase().includes(q) || p.avixaProductName?.toLowerCase().includes(q) || p.productGroupName?.toLowerCase().includes(q); }
                        return true;
                      });
                      const getProductMarketNames = (p) => (p.markets || '').split(', ').map(s => s.trim()).filter(Boolean);
                      const cellInput = (width = '130px') => ({ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '3px 6px', fontSize: '12px', width });
                      return (
                        <div style={styles.card}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                            <h3 style={{ ...styles.cardTitle, margin: 0 }}>💊 Ürünler ({filteredProducts.length} / {products.length})</h3>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                              <select style={{ ...styles.input, minWidth: '140px', fontSize: '13px', padding: '6px 10px' }} value={productTeamFilter} onChange={e => { setProductTeamFilter(e.target.value); setProductMarketFilter(''); }}><option value="">Tüm Ekipler</option>{teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}</select>
                              <select style={{ ...styles.input, minWidth: '160px', fontSize: '13px', padding: '6px 10px' }} value={productMarketFilter} onChange={e => setProductMarketFilter(e.target.value)}><option value="">Tüm Pazarlar</option>{(productTeamFilter ? markets.filter(m => String(m.teamId) === String(productTeamFilter)) : markets).map(m => <option key={m.id} value={m.marketName}>{m.marketName}</option>)}</select>
                              <input placeholder="🔍 Ürün ara..." value={productSearch} onChange={e => setProductSearch(e.target.value)} style={{ ...styles.searchInput, width: '180px' }} />
                              <button style={{ ...styles.primaryButton, backgroundColor: '#16a34a' }} onClick={() => setShowNewProductRow(v => !v)}>{showNewProductRow ? 'İptal' : '+ Yeni Ürün'}</button>
                              <button style={styles.primaryButton} onClick={async () => {
                                try {
                                  const changed = products.filter(p => p._dirty);
                                  if (changed.length === 0) { setMessage('Değişiklik yok.'); return; }
                                  for (const p of changed) await updateProduct(p.id, { avixaProductName: p.avixaProductName, productGroupName: p.productGroupName, isVisible: !!p.isVisible, isAvixaProduct: !!p.isAvixaProduct, suMultiplier: p.suMultiplier, groupType: p.groupType, promoFlag: p.promoFlag, showInWeeklyDashboard: !!p.showInWeeklyDashboard, showInMonthlyDashboard: !!p.showInMonthlyDashboard }, isSuperAdmin ? mdTenantId : user?.tenantId);
                                  setMessage(`${changed.length} ürün güncellendi.`); loadMasterData(isSuperAdmin ? mdTenantId : null);
                                } catch { setMessage('İşlem başarısız.'); }
                              }}>💾 Kaydet</button>
                            </div>
                          </div>
                          {showNewProductRow && (
                            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                              <p style={{ ...styles.label, fontWeight: '600', marginBottom: '12px', color: '#16a34a' }}>➕ Yeni Ürün</p>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                                {[{ label: 'IMS Ürün Adı *', key: 'imsProductName', ph: 'Zorunlu', w: '180px' }, { label: 'Şirket Ürün Adı', key: 'avixaProductName', w: '160px' }, { label: 'Ürün Grubu', key: 'productGroupName', w: '130px' }].map(({ label, key, ph, w }) => (
                                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label style={styles.label}>{label}</label><input value={newProductRow[key]} onChange={e => setNewProductRow(v => ({ ...v, [key]: e.target.value }))} style={{ ...styles.input, minWidth: w }} placeholder={ph} /></div>
                                ))}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label style={styles.label}>SU Çarpan</label><input type="number" step="0.01" value={newProductRow.suMultiplier} onChange={e => setNewProductRow(v => ({ ...v, suMultiplier: parseFloat(e.target.value) || 1 }))} style={{ ...styles.input, width: '80px' }} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label style={styles.label}>{pm('group')}</label>{groupTypeIsText ? <input value={newProductRow.groupType || ''} onChange={e => setNewProductRow(v => ({ ...v, groupType: e.target.value || null }))} style={{ ...styles.input, minWidth: '160px' }} maxLength={128} /> : <select value={newProductRow.groupType || ''} onChange={e => setNewProductRow(v => ({ ...v, groupType: e.target.value || null }))} style={styles.input}><option value="">-</option><option value="Marketing">Marketing</option><option value="AVİXA">AVİXA</option></select>}</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label style={styles.label}>{pm('promoNonPromo')}</label><select value={newProductRow.promoFlag || ''} onChange={e => setNewProductRow(v => ({ ...v, promoFlag: e.target.value || null }))} style={styles.input}>{promoIsFocusBrand ? <><option value="">-</option><option value="1">Focus</option><option value="0">Non-Focus</option></> : <><option value="">-</option><option value="Promo">Promo</option><option value="Non-Promo">Non-Promo</option></>}</select></div>
                                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', paddingBottom: '2px' }}>
                                  {[{ key: 'isVisible', label: 'Görünür' }, { key: 'isAvixaProduct', label: pm('isAvixaProduct') }, { key: 'showInWeeklyDashboard', label: 'Haftalık' }, { key: 'showInMonthlyDashboard', label: 'Aylık' }].map(({ key, label }) => (
                                    <label key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#64748b', cursor: 'pointer' }}>{label}<input type="checkbox" checked={!!newProductRow[key]} onChange={e => setNewProductRow(v => ({ ...v, [key]: e.target.checked }))} /></label>
                                  ))}
                                </div>
                                <button style={{ ...styles.primaryButton, backgroundColor: '#16a34a' }} onClick={async () => {
                                  if (!newProductRow.imsProductName.trim()) { setMessage('IMS Ürün Adı zorunludur.'); return; }
                                  try { await createProduct({ imsProductName: newProductRow.imsProductName.trim(), avixaProductName: newProductRow.avixaProductName || null, productGroupName: newProductRow.productGroupName || null, isVisible: newProductRow.isVisible, isAvixaProduct: newProductRow.isAvixaProduct, suMultiplier: newProductRow.suMultiplier, groupType: newProductRow.groupType || null, promoFlag: newProductRow.promoFlag || null, showInWeeklyDashboard: newProductRow.showInWeeklyDashboard, showInMonthlyDashboard: newProductRow.showInMonthlyDashboard }, isSuperAdmin ? mdTenantId : user?.tenantId); setMessage('Ürün oluşturuldu.'); setShowNewProductRow(false); setNewProductRow({ imsProductName: '', avixaProductName: '', productGroupName: '', isVisible: true, isAvixaProduct: false, suMultiplier: 1, groupType: '', promoFlag: '', showInWeeklyDashboard: true, showInMonthlyDashboard: true }); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch (err) { setMessage(err?.response?.data?.message || 'İşlem başarısız.'); }
                                }}>Oluştur</button>
                              </div>
                            </div>
                          )}
                          <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>Hücreleri tıklayarak düzenleyin. Değişen satırlar sarı ile işaretlenir.</p>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ ...styles.table, fontSize: '12px' }}>
                              <thead><tr><th style={styles.th}>IMS Ürün Adı</th><th style={styles.th}>{pm('avixaProductName')}</th><th style={styles.th}>Ürün Grubu</th><th style={{ ...styles.th, textAlign: 'center' }}>Görünür</th><th style={{ ...styles.th, textAlign: 'center' }}>{pm('isAvixaProduct')}</th><th style={styles.th}>SU Çarpan</th><th style={styles.th}>{pm('group')}</th><th style={styles.th}>{pm('promoNonPromo')}</th><th style={{ ...styles.th, textAlign: 'center' }}>Haftalık</th><th style={{ ...styles.th, textAlign: 'center' }}>Aylık</th><th style={styles.th}>Pazarlar</th><th style={styles.th}>İşlem</th></tr></thead>
                              <tbody>
                                {filteredProducts.map(p => {
                                  const realIdx = products.findIndex(pr => pr.id === p.id);
                                  const pmn = getProductMarketNames(p);
                                  return (
                                    <tr key={p.id} style={{ backgroundColor: p._dirty ? '#fefce8' : 'transparent' }}>
                                      <td style={{ ...styles.td, color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap' }}>{p.imsProductName}</td>
                                      <td style={styles.td}><input value={p.avixaProductName || ''} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], avixaProductName: e.target.value, _dirty: true }; setProducts(u); }} style={cellInput('130px')} /></td>
                                      <td style={styles.td}><input value={p.productGroupName || ''} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], productGroupName: e.target.value, _dirty: true }; setProducts(u); }} style={cellInput('120px')} /></td>
                                      <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={!!p.isVisible} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], isVisible: e.target.checked, _dirty: true }; setProducts(u); }} /></td>
                                      <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={!!p.isAvixaProduct} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], isAvixaProduct: e.target.checked, _dirty: true }; setProducts(u); }} /></td>
                                      <td style={styles.td}><input type="number" step="0.01" value={p.suMultiplier} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], suMultiplier: parseFloat(e.target.value) || 1, _dirty: true }; setProducts(u); }} style={cellInput('65px')} /></td>
                                      <td style={styles.td}>{groupTypeIsText ? <input value={p.groupType || ''} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], groupType: e.target.value || null, _dirty: true }; setProducts(u); }} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '3px 4px', fontSize: '12px', minWidth: '120px' }} maxLength={128} /> : <select value={p.groupType || ''} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], groupType: e.target.value || null, _dirty: true }; setProducts(u); }} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '3px 4px', fontSize: '12px' }}><option value="">-</option><option value="Marketing">Marketing</option><option value="AVİXA">AVİXA</option></select>}</td>
                                      <td style={styles.td}><select value={p.promoFlag || ''} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], promoFlag: e.target.value || null, _dirty: true }; setProducts(u); }} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '3px 4px', fontSize: '12px' }}>{promoIsFocusBrand ? <><option value="">-</option><option value="1">Focus</option><option value="0">Non-Focus</option></> : <><option value="">-</option><option value="Promo">Promo</option><option value="Non-Promo">Non-Promo</option></>}</select></td>
                                      <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={!!p.showInWeeklyDashboard} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], showInWeeklyDashboard: e.target.checked, _dirty: true }; setProducts(u); }} /></td>
                                      <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={!!p.showInMonthlyDashboard} onChange={e => { const u = [...products]; u[realIdx] = { ...u[realIdx], showInMonthlyDashboard: e.target.checked, _dirty: true }; setProducts(u); }} /></td>
                                      <td style={{ ...styles.td, minWidth: '180px' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>
                                          {pmn.map(mn => { const mkt = markets.find(m => m.marketName === mn); return <span key={mn} style={{ ...styles.tag, fontSize: '11px', padding: '2px 6px' }}>{mn}{mkt?.teamName && <span style={{ color: '#94a3b8', marginLeft: '3px' }}>({mkt.teamName})</span>}<button style={styles.tagRemove} onClick={async () => { if (!mkt) return; try { await removeProductFromMarket(p.id, mkt.id, isSuperAdmin ? mdTenantId : null); setMessage(`"${mn}" pazarından çıkarıldı.`); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch (err) { setMessage(apiErrorMessage(err)); } }}>✕</button></span>; })}
                                        </div>
                                        <MarketPicker
                                          options={markets
                                            .filter(m => !pmn.includes(m.marketName) && (!productTeamFilter || String(m.teamId) === String(productTeamFilter)))
                                            .slice()
                                            .sort((a, b) => a.marketName.localeCompare(b.marketName, 'tr'))}
                                          onPick={async marketId => { try { await assignProductToMarket(p.id, marketId, isSuperAdmin ? mdTenantId : null); setMessage('Pazar eklendi.'); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch { setMessage('İşlem başarısız.'); } }}
                                        />
                                      </td>
                                      <td style={styles.td}><button style={styles.dangerButton} onClick={async () => { if (!window.confirm(`"${p.imsProductName}" ürününü silmek istiyor musunuz?`)) return; try { await deleteProduct(p.id, isSuperAdmin ? mdTenantId : null); setMessage('Ürün silindi.'); loadMasterData(isSuperAdmin ? mdTenantId : null); } catch (err) { setMessage(apiErrorMessage(err)); } }}>Sil</button></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                    {mdSubTab === 'product-targets' && (
                      <ProductTargetPanel
                        teams={teams}
                        tid={isSuperAdmin ? mdTenantId : null}
                        isSuperAdmin={isSuperAdmin}
                        setMessage={setMessage}
                        styles={styles}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ETL */}
        {activeTab === 'etl' && !loading && (
          <EtlPage />
        )}

        {/* HEDEF DAĞILIMI */}
        {activeTab === 'target-distribution' && !loading && (
          <div>
            {isSuperAdmin && (
              <div style={styles.card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ ...styles.label, margin: 0, fontWeight: '600' }}>Tenant Seç:</label>
                  <select style={{ ...styles.input, minWidth: '250px' }} value={mdTenantId} onChange={e => setMdTenantId(e.target.value)}>
                    <option value="">Tenant Seçin...</option>
                    {tenants.filter(t => t.isActive).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
            )}
            <TargetDistributionPanel
              tid={isSuperAdmin ? mdTenantId : (user?.tenantId ?? null)}
              isSuperAdmin={isSuperAdmin}
              setMessage={setMessage}
              styles={styles}
            />
          </div>
        )}

        {/* TENANTS */}
        {activeTab === 'tenants' && isSuperAdmin && !loading && (
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>{editingTenant ? 'Tenant Düzenle' : 'Yeni Tenant Ekle'}</h3>
              <form onSubmit={handleCreateTenant} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Temel bilgiler */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <input placeholder="Tenant Adı" value={newTenantName} onChange={e => setNewTenantName(e.target.value)} style={styles.input} required />
                  <input placeholder="Açıklama (opsiyonel)" value={newTenantDesc} onChange={e => setNewTenantDesc(e.target.value)} style={{ ...styles.input, flex: 1 }} />
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <input placeholder="Power BI Workspace ID (prod)" value={newTenantWorkspaceId} onChange={e => setNewTenantWorkspaceId(e.target.value)} style={{ ...styles.input, flex: 1, fontFamily: 'monospace' }} required />
                  <input placeholder="Power BI Workspace ID (test, opsiyonel)" value={newTenantTestWorkspaceId} onChange={e => setNewTenantTestWorkspaceId(e.target.value)} style={{ ...styles.input, flex: 1, fontFamily: 'monospace' }} />
                </div>

                {/* Marka Ayarları */}
                <p style={{ ...styles.label, marginBottom: '0', fontWeight: '600' }}>🎨 Marka Ayarları</p>

                {/* 3 renk seçici */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {[
                    { label: 'Arka Plan Rengi', value: newTenantPrimaryColor, set: setNewTenantPrimaryColor },
                    { label: 'Vurgu Rengi', value: newTenantSecondaryColor, set: setNewTenantSecondaryColor },
                    { label: 'Yazı Rengi', value: newTenantTextColor, set: setNewTenantTextColor },
                  ].map(({ label, value, set }) => (
                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={styles.label}>{label}</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="color" value={value} onChange={e => set(e.target.value)} style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }} />
                        <input value={value} onChange={e => set(e.target.value)} style={{ ...styles.input, minWidth: '90px', fontFamily: 'monospace' }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Yazı tipi + Önizleme */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={styles.label}>Yazı Boyutu (px)</label>
                    <input type="number" min="10" max="32" value={newTenantFontSize} onChange={e => setNewTenantFontSize(parseInt(e.target.value) || 16)} style={{ ...styles.input, minWidth: '80px', maxWidth: '100px' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', paddingBottom: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                      <input type="checkbox" checked={newTenantFontBold} onChange={e => setNewTenantFontBold(e.target.checked)} />
                      <strong>Kalın (Bold)</strong>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                      <input type="checkbox" checked={newTenantFontItalic} onChange={e => setNewTenantFontItalic(e.target.checked)} />
                      <em>İtalik</em>
                    </label>
                  </div>
                  {/* Canlı Önizleme */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={styles.label}>Önizleme</label>
                    <div style={{ backgroundColor: newTenantPrimaryColor, padding: '8px 16px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '200px' }}>
                      <span style={{ color: newTenantTextColor, fontWeight: newTenantFontBold ? '700' : '400', fontStyle: newTenantFontItalic ? 'italic' : 'normal', fontSize: `${newTenantFontSize}px` }}>
                        {newTenantPortalTitle || 'Portal Başlığı'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Portal başlığı */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={styles.label}>Portal Başlığı</label>
                    <input placeholder="Avixa Analitik Portal" value={newTenantPortalTitle} onChange={e => setNewTenantPortalTitle(e.target.value)} style={styles.input} />
                  </div>
                </div>

                {/* Logo / Favicon */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={styles.label}>Logo URL</label>
                    <input placeholder="https://..." value={newTenantLogoUrl} onChange={e => setNewTenantLogoUrl(e.target.value)} style={styles.input} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={styles.label}>Favicon URL</label>
                    <input placeholder="https://..." value={newTenantFaviconUrl} onChange={e => setNewTenantFaviconUrl(e.target.value)} style={styles.input} />
                  </div>
                </div>

                {/* Tenant uygulamaları (registry) — yalnızca düzenlemede */}
                {editingTenant && (
                  <div>
                    <p style={{ ...styles.label, marginBottom: '6px', fontWeight: '600' }}>🧩 Uygulamalar</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {editingTenantApps.map(a => (
                        <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div onClick={() => handleToggleTenantApp(a.key, !a.isEnabled)}
                            style={{ width: '36px', height: '20px', backgroundColor: a.isEnabled ? '#1a1a2e' : '#d1d5db', borderRadius: '10px', position: 'relative', flexShrink: 0, cursor: 'pointer' }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: a.isEnabled ? '18px' : '2px', transition: 'left 0.2s' }} />
                          </div>
                          <span style={{ fontSize: '14px', color: '#1e293b' }}>{a.name}</span>
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{a.isEnabled ? 'Açık' : 'Kapalı'}</span>
                        </div>
                      ))}
                      {editingTenantApps.length === 0 && <span style={{ fontSize: '13px', color: '#94a3b8' }}>Uygulama yok.</span>}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" style={styles.primaryButton}>{editingTenant ? 'Güncelle' : 'Oluştur'}</button>
                  {editingTenant && (
                    <button type="button" style={styles.dangerButton} onClick={() => { setEditingTenant(null); resetTenantForm(); }}>İptal</button>
                  )}
                </div>
              </form>
            </div>

            <div style={styles.card}>
              <div style={styles.tableHeader}>
                <h3 style={styles.cardTitle}>Tenants ({filteredTenants.length})</h3>
                <input placeholder="🔍 Tenant ara..." value={tenantSearch} onChange={e => { setTenantSearch(e.target.value); setTenantPage(1); }} style={styles.searchInput} />
              </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Tenant Adı</th><th style={styles.th}>Açıklama</th>
                    <th style={styles.th}>Prod Workspace</th><th style={styles.th}>Test Workspace</th>
                    <th style={styles.th}>Kullanıcı</th><th style={styles.th}>Rapor</th>
                    <th style={styles.th}>Marka</th><th style={styles.th}>Durum</th><th style={styles.th}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredTenants, tenantPage).map(t => (
                    <tr key={t.id}>
                      <td style={styles.td}><strong>{t.name}</strong></td>
                      <td style={styles.td}>{t.description || '-'}</td>
                      <td style={styles.td} title={t.powerBiWorkspaceId}><span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{t.powerBiWorkspaceId?.substring(0, 8)}...</span></td>
                      <td style={styles.td} title={t.powerBiTestWorkspaceId}>{t.powerBiTestWorkspaceId ? <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{t.powerBiTestWorkspaceId?.substring(0, 8)}...</span> : <span style={{ color: '#94a3b8' }}>-</span>}</td>
                      <td style={styles.td}><span style={styles.badge}>{t.userCount}</span></td>
                      <td style={styles.td}><span style={styles.badge}>{t.reportCount}</span></td>
                      {/* Marka önizleme — küçük */}
                      <td style={styles.td}>
                        <div style={{ backgroundColor: t.primaryColor || '#1a1a2e', padding: '3px 8px', borderRadius: '4px', display: 'inline-block' }}>
                          <span style={{ color: t.textColor || '#ffffff', fontSize: `${Math.min(t.fontSize || 13, 13)}px`, fontWeight: t.fontBold ? '700' : '400', fontStyle: t.fontItalic ? 'italic' : 'normal', whiteSpace: 'nowrap' }}>
                            {t.portalTitle || t.name}
                          </span>
                        </div>
                      </td>
                      <td style={styles.td}><span style={{ color: t.isActive ? 'green' : 'red', fontWeight: '500' }}>{t.isActive ? 'Aktif' : 'Pasif'}</span></td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => handleEditTenant(t)} style={{ ...styles.primaryButton, fontSize: '12px', padding: '4px 10px' }}>Düzenle</button>
                          <button onClick={() => handleToggleTenant(t.id)} style={{ ...styles.primaryButton, backgroundColor: t.isActive ? '#f59e0b' : '#22c55e', fontSize: '12px', padding: '4px 10px' }}>{t.isActive ? 'Pasif Et' : 'Aktif Et'}</button>
                          <button onClick={() => handleDeleteTenant(t.id)} style={styles.dangerButton}>Sil</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination currentPage={tenantPage} total={totalPages(filteredTenants)} onPageChange={setTenantPage} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  title: { color: 'white', margin: 0, fontSize: '18px' },
  superAdminBadge: { backgroundColor: '#f59e0b', color: '#1a1a2e', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' },
  tenantBadge: { backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' },
  userName: { color: '#94a3b8', fontSize: '14px' },
  navButton: { backgroundColor: 'transparent', border: '1px solid #94a3b8', color: '#94a3b8', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid white', color: 'white', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  tabs: { backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', padding: '0 24px', display: 'flex' },
  tab: { padding: '14px 20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '14px', color: '#64748b', borderBottom: '2px solid transparent' },
  tabActive: { color: '#1a1a2e', borderBottom: '2px solid #1a1a2e', fontWeight: '600' },
  message: { margin: '16px 24px', padding: '12px 16px', backgroundColor: '#dcfce7', borderRadius: '6px', color: '#166534', fontSize: '14px', cursor: 'pointer' },
  content: { padding: '24px' },
  card: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  cardTitle: { margin: '0 0 16px 0', color: '#1a1a2e', fontSize: '16px' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  searchInput: { padding: '8px 12px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '14px', width: '280px' },
  form: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  input: { padding: '8px 12px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '14px', minWidth: '200px' },
  primaryButton: { padding: '8px 16px', backgroundColor: '#1a1a2e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  dangerButton: { padding: '4px 10px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px', borderBottom: '2px solid #e2e8f0', fontSize: '13px', color: '#64748b' },
  td: { padding: '10px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', verticalAlign: 'top' },
  groupCard: { border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '12px', overflow: 'hidden' },
  groupHeader: { padding: '12px 16px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', gap: '12px' },
  groupDesc: { color: '#94a3b8', fontSize: '13px', flex: 1 },
  groupBody: { padding: '16px' },
  badge: { backgroundColor: '#e2e8f0', borderRadius: '12px', padding: '2px 8px', fontSize: '12px' },
  label: { margin: '0 0 6px 0', fontSize: '12px', color: '#64748b' },
  tag: { backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  tagRemove: { background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '10px', padding: '0' },
  selectSmall: { padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', cursor: 'pointer' },
  pagination: { display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '16px' },
  pageButton: { padding: '6px 10px', border: '1px solid #d1d5db', backgroundColor: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  pageButtonActive: { backgroundColor: '#1a1a2e', color: 'white', borderColor: '#1a1a2e' },
  feedbackItem: { padding: '12px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '8px', cursor: 'pointer', transition: 'background-color 0.15s' },
  feedbackItemActive: { backgroundColor: '#eff6ff', borderColor: '#3b82f6' },
  feedbackDetail: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px' },
  detailRow: { display: 'flex', gap: '8px', fontSize: '13px' },
  detailLabel: { color: '#64748b', minWidth: '80px', fontWeight: '500' },
  detailValue: { color: '#1e293b' },
  detailSection: { marginBottom: '12px' },
  detailSectionTitle: { margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#64748b' },
  detailBox: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: '1.5' },
};

export default AdminPage;